import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, QrCode, MessageCircle, Settings, Shield, LogOut, Camera, Send, AlertCircle, Phone, Lock, Star, Crown, Zap } from 'lucide-react';
import { auth as firebaseAuth } from './lib/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber, signOut } from 'firebase/auth';

// ============================================
// API CLIENT
// ============================================

// Prioridade: window.EAT_API_URL (definido no index.html) > env > localhost
const API_BASE = (typeof window !== 'undefined' && window.EAT_API_URL && window.EAT_API_URL !== 'RAILWAY_URL_AQUI')
  ? window.EAT_API_URL
  : (process.env.REACT_APP_API_URL || 'http://localhost:3000');

function getDeviceId() {
  let id = localStorage.getItem('eat_device_id');
  if (!id) {
    id = 'dev_' + Math.random().toString(36).slice(2, 18) + Date.now().toString(36);
    localStorage.setItem('eat_device_id', id);
  }
  return id;
}

async function apiRequest(method, endpoint, body, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Erro inesperado');
    err.statusCode = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

const api = {
  post: (endpoint, body, token)  => apiRequest('POST',   endpoint, body, token),
  get:  (endpoint, token)        => apiRequest('GET',    endpoint, null, token),
  put:  (endpoint, body, token)  => apiRequest('PUT',    endpoint, body, token),
  del:  (endpoint, token)        => apiRequest('DELETE', endpoint, null, token),
};

// ============================================
// DESIGN SYSTEM
// ============================================

const colors = {
  white: '#FFFFFF',
  black: '#1A1A1A',
  blue: '#2563EB',
  blueLight: '#60A5FA',
  gold: '#D4AF37',
  goldLight: '#F0E68C',
  green: '#10B981',
  greenLight: '#6EE7B7',
  yellow: '#F59E0B',
  yellowLight: '#FCD34D',
  red: '#EF4444',
  redLight: '#FCA5A5',
  gray: '#F3F4F6',
  grayDark: '#6B7280'
};

const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px'
};

// ============================================
// COMPONENTES BASE
// ============================================

const Container = ({ children, style }) => (
  <div style={{
    maxWidth: '480px',
    margin: '0 auto',
    backgroundColor: colors.white,
    minHeight: '100vh',
    position: 'relative',
    ...style
  }}>
    {children}
  </div>
);

const Header = ({ title, onBack, actions }) => (
  <header style={{
    padding: spacing.md,
    borderBottom: `1px solid ${colors.gray}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.white,
    position: 'sticky',
    top: 0,
    zIndex: 100
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
      {onBack && (
        <button onClick={onBack} style={{
          background: 'none',
          border: 'none',
          fontSize: '24px',
          cursor: 'pointer',
          color: colors.black
        }}>←</button>
      )}
      <div style={{
        fontSize: '20px',
        fontWeight: '600',
        margin: 0,
        color: colors.black,
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm
      }}>{title}</div>
    </div>
    {actions && <div style={{ display: 'flex', gap: spacing.sm }}>{actions}</div>}
  </header>
);

const Button = ({ children, variant = 'primary', fullWidth, onClick, disabled, style }) => {
  const variants = {
    primary: {
      backgroundColor: colors.blue,
      color: colors.white,
      border: 'none'
    },
    secondary: {
      backgroundColor: colors.white,
      color: colors.blue,
      border: `2px solid ${colors.blue}`
    },
    ghost: {
      backgroundColor: 'transparent',
      color: colors.black,
      border: 'none'
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: `${spacing.md} ${spacing.lg}`,
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        width: fullWidth ? '100%' : 'auto',
        transition: 'all 0.2s',
        ...variants[variant],
        ...style
      }}
    >
      {children}
    </button>
  );
};

const StatusIndicator = ({ status, size = 'md', showLabel = false }) => {
  const statusConfig = {
    green: { color: colors.green, label: 'Disponível' },
    yellow: { color: colors.yellow, label: 'Talvez' },
    red: { color: colors.red, label: 'Não disponível' }
  };

  const sizes = {
    sm: '12px',
    md: '16px',
    lg: '24px'
  };

  const config = statusConfig[status] || statusConfig.red;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
      <div style={{
        width: sizes[size],
        height: sizes[size],
        borderRadius: '50%',
        backgroundColor: config.color,
        boxShadow: `0 0 8px ${config.color}50`
      }} />
      {showLabel && (
        <span style={{
          fontSize: '14px',
          color: colors.grayDark,
          fontWeight: '500'
        }}>{config.label}</span>
      )}
    </div>
  );
};

// ============================================
// TELA 1: QR CODE SCAN (Entrada)
// ============================================

// Extrai o código do venue de uma URL EAT ou do texto cru
function extractVenueCode(raw) {
  const urlMatch = raw.match(/check-in\/(VENUE_[A-Z0-9_]+)/);
  if (urlMatch) return urlMatch[1];
  if (/^VENUE_[A-Z0-9_]+$/.test(raw)) return raw;
  return null;
}

const QRScanScreen = ({ onScan, error: externalError }) => {
  const videoRef  = useRef(null);
  const animRef   = useRef(null);
  const detRef    = useRef(null);
  const [scanning,     setScanning]     = useState(false);
  const [cameraError,  setCameraError]  = useState(null);
  const [manualCode,   setManualCode]   = useState('');
  const [showManual,   setShowManual]   = useState(false);

  const stopCamera = useCallback(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const scanFrame = useCallback(() => {
    if (!videoRef.current || !detRef.current) return;
    detRef.current.detect(videoRef.current)
      .then(barcodes => {
        if (barcodes.length > 0) {
          const code = extractVenueCode(barcodes[0].rawValue);
          if (code) { stopCamera(); onScan(code); return; }
        }
        animRef.current = requestAnimationFrame(scanFrame);
      })
      .catch(() => { animRef.current = requestAnimationFrame(scanFrame); });
  }, [onScan, stopCamera]);

  const startCamera = useCallback(async () => {
    setScanning(true);
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      if ('BarcodeDetector' in window) {
        detRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
        animRef.current = requestAnimationFrame(scanFrame);
      } else {
        setCameraError('Câmera aberta — mas o seu navegador não suporta detecção automática de QR. Use o campo manual abaixo.');
      }
    } catch (e) {
      setCameraError('Não foi possível acessar a câmera: ' + e.message);
      setScanning(false);
    }
  }, [scanFrame]);

  const handleManualSubmit = () => {
    const code = extractVenueCode(manualCode.trim());
    if (code) onScan(code);
    else setCameraError('Código inválido. Cole o código ou URL do QR do estabelecimento.');
  };

  const displayError = externalError || cameraError;

  return (
    <Container>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: spacing.xl, textAlign: 'center' }}>

        {/* Preview da câmera */}
        {scanning && (
          <div style={{ position: 'relative', width: '100%', maxWidth: '360px', marginBottom: spacing.xl, borderRadius: '20px', overflow: 'hidden', backgroundColor: colors.black }}>
            <video ref={videoRef} playsInline muted style={{ width: '100%', display: 'block', borderRadius: '20px' }} />
            {/* Overlay com mira */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: '200px', height: '200px', border: '3px solid ' + colors.blue, borderRadius: '16px', boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)' }} />
            </div>
            <button onClick={stopCamera} style={{ position: 'absolute', top: spacing.md, right: spacing.md, background: 'rgba(0,0,0,0.6)', border: 'none', color: colors.white, borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', fontSize: '18px' }}>✕</button>
          </div>
        )}

        {!scanning && (
          <>
            <div style={{ width: '120px', height: '120px', borderRadius: '24px', backgroundColor: colors.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: spacing.xl }}>
              <QrCode size={64} color={colors.white} />
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: colors.black, marginBottom: spacing.md }}>EAT</h1>
            <p style={{ fontSize: '16px', color: colors.grayDark, marginBottom: spacing.xxl, lineHeight: '1.6' }}>
              Escaneie o QR Code do estabelecimento para fazer check-in e ver quem está por perto
            </p>
          </>
        )}

        {displayError && (
          <div style={{ width: '100%', marginBottom: spacing.md, padding: spacing.md, backgroundColor: colors.red + '15', borderRadius: '12px', display: 'flex', gap: spacing.sm, alignItems: 'flex-start' }}>
            <AlertCircle size={16} color={colors.red} style={{ flexShrink: 0, marginTop: 2 }} />
            <span style={{ fontSize: '14px', color: colors.red, textAlign: 'left' }}>{displayError}</span>
          </div>
        )}

        {!scanning && (
          <Button onClick={startCamera} fullWidth style={{ marginBottom: spacing.md }}>
            <Camera size={20} style={{ marginRight: spacing.sm, verticalAlign: 'middle' }} />
            Abrir câmera e escanear
          </Button>
        )}

        {/* Entrada manual (demo / fallback) */}
        <button onClick={() => setShowManual(v => !v)} style={{ background: 'none', border: 'none', color: colors.blue, fontSize: '14px', cursor: 'pointer', marginBottom: spacing.md }}>
          {showManual ? 'Fechar entrada manual' : 'Inserir código manualmente (demo)'}
        </button>

        {showManual && (
          <div style={{ width: '100%', display: 'flex', gap: spacing.sm, marginBottom: spacing.md }}>
            <input
              type="text"
              placeholder="Cole o código ou URL do QR..."
              value={manualCode}
              onChange={e => setManualCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleManualSubmit()}
              style={{ flex: 1, padding: spacing.md, borderRadius: '12px', border: `1px solid ${colors.gray}`, fontSize: '14px', outline: 'none' }}
            />
            <Button onClick={handleManualSubmit} style={{ whiteSpace: 'nowrap' }}>OK</Button>
          </div>
        )}

        <div style={{ marginTop: spacing.md, padding: spacing.lg, backgroundColor: colors.gray, borderRadius: '12px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.md }}>
            <Shield size={20} color={colors.blue} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '14px', color: colors.grayDark, lineHeight: '1.5', textAlign: 'left' }}>
              <strong style={{ color: colors.black, display: 'block', marginBottom: spacing.xs }}>Privado e respeitoso</strong>
              Só pessoas no mesmo local podem se conectar. Seu status é sempre sua escolha.
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

// ============================================
// TELA 2: HOME (Após check-in)
// ============================================

const HomeScreen = ({ location, userStatus, user, onStatusChange, people, onPersonClick }) => {
  // Filtrar pessoas baseado nos símbolos do usuário:
  // - Se tem abacaxi: vê APENAS pessoas com abacaxi
  // - Se tem arco-íris: vê APENAS pessoas com arco-íris
  // - Se não tem nenhum: vê APENAS pessoas sem símbolos
  const filteredPeople = people.filter(person => {
    // Se usuário tem abacaxi, mostra só quem tem abacaxi
    if (user.abacaxi) {
      return person.abacaxi;
    }
    // Se usuário tem arco-íris, mostra só quem tem arco-íris
    if (user.arcoIris) {
      return person.arcoIris;
    }
    // Se usuário não tem nenhum símbolo, mostra só quem também não tem
    return !person.abacaxi && !person.arcoIris;
  });

  return (
    <Container>
      <Header 
        title="EAT"
        actions={
          <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <Settings size={24} color={colors.black} />
          </button>
        }
      />

      {/* Status do Usuário - Semáforo Social */}
      <div style={{
        padding: spacing.lg,
        backgroundColor: colors.gray,
        borderBottom: `1px solid ${colors.gray}`
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.md
        }}>
          <div>
            <p style={{
              fontSize: '14px',
              color: colors.grayDark,
              margin: 0,
              marginBottom: spacing.xs
            }}>Você está em</p>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: colors.black,
              margin: 0
            }}>{location}</h2>
          </div>
          <StatusIndicator status={userStatus} size="lg" />
        </div>

        <p style={{
          fontSize: '14px',
          color: colors.grayDark,
          marginBottom: spacing.md
        }}>Seu status atual</p>

        <div style={{ display: 'flex', gap: spacing.sm }}>
          {['green', 'yellow', 'red'].map((status) => (
            <button
              key={status}
              onClick={() => onStatusChange(status)}
              style={{
                flex: 1,
                padding: spacing.md,
                borderRadius: '12px',
                border: `2px solid ${userStatus === status ? colors.black : colors.gray}`,
                backgroundColor: userStatus === status ? colors.white : colors.gray,
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: spacing.xs
              }}
            >
              <StatusIndicator status={status} size="md" />
              <span style={{
                fontSize: '12px',
                color: colors.black,
                fontWeight: userStatus === status ? '600' : '400'
              }}>
                {status === 'green' ? 'Aberto' : status === 'yellow' ? 'Talvez' : 'Ocupado'}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Lista de Pessoas Filtradas */}
      <div style={{ padding: spacing.lg }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: colors.black,
          marginBottom: spacing.md
        }}>Pessoas por perto ({filteredPeople.length})</h3>

        {filteredPeople.length === 0 ? (
          <div style={{
            padding: spacing.xxl,
            textAlign: 'center',
            color: colors.grayDark
          }}>
            <p>Nenhuma pessoa compatível por perto no momento</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
            {filteredPeople.map((person) => (
              <div
                key={person.id}
                onClick={() => onPersonClick(person)}
                style={{
                  padding: spacing.md,
                  backgroundColor: colors.white,
                  border: `1px solid ${colors.gray}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.md
                }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  backgroundColor: colors.blue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.white,
                  fontSize: '20px',
                  fontWeight: '600',
                  position: 'relative'
                }}>
                  {(person.name || '?').charAt(0)}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    backgroundColor: person.status === 'green' ? colors.green : 
                                    person.status === 'yellow' ? colors.yellow : colors.red,
                    border: `2px solid ${colors.white}`
                  }} />
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: '2px' }}>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: colors.black
                    }}>{person.name}</span>
                    {person.abacaxi && (
                      <span style={{ fontSize: '18px', display: 'inline-block', transform: 'rotate(180deg)' }}>🍍</span>
                    )}
                    {person.arcoIris && (
                      <span style={{ fontSize: '18px' }}>🏳️‍🌈</span>
                    )}
                    {person.premium && (
                      <span style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        backgroundColor: colors.gold,
                        color: colors.white,
                        borderRadius: '4px',
                        fontWeight: '600'
                      }}>PRO</span>
                    )}
                  </div>
                  <span style={{
                    fontSize: '14px',
                    color: colors.grayDark
                  }}>
                    {person.status === 'green' ? 'Disponível para conversar' :
                     person.status === 'yellow' ? 'Talvez disponível' :
                     'Não disponível no momento'}
                  </span>
                </div>

                <MessageCircle size={20} color={colors.blue} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Container>
  );
};

// ============================================
// TELA 3: PERFIL DE USUÁRIO
// ============================================

const ProfileScreen = ({ person, onStartChat, onReport, onBack }) => {
  return (
    <Container>
      <Header title="Perfil" onBack={onBack} />

      <div style={{
        padding: spacing.xl,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center'
      }}>
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          backgroundColor: colors.blue,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.white,
          fontSize: '48px',
          fontWeight: '600',
          marginBottom: spacing.lg,
          position: 'relative'
        }}>
          {(person.name || '?').charAt(0)}
          <div style={{
            position: 'absolute',
            bottom: '8px',
            right: '8px',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: person.status === 'green' ? colors.green :
                            person.status === 'yellow' ? colors.yellow : colors.red,
            border: `3px solid ${colors.white}`
          }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: colors.black,
            margin: 0
          }}>{person.name}</h2>
          {person.abacaxi && <span style={{ fontSize: '24px', display: 'inline-block', transform: 'rotate(180deg)' }}>🍍</span>}
          {person.arcoIris && <span style={{ fontSize: '24px' }}>🏳️‍🌈</span>}
        </div>

        {person.premium && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: spacing.xs,
            padding: `${spacing.xs} ${spacing.md}`,
            backgroundColor: colors.gold,
            color: colors.white,
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            marginBottom: spacing.lg
          }}>
            ✨ Membro Premium
          </div>
        )}

        <StatusIndicator status={person.status} size="lg" showLabel />

        <div style={{
          marginTop: spacing.xl,
          padding: spacing.lg,
          backgroundColor: colors.gray,
          borderRadius: '12px',
          width: '100%',
          textAlign: 'left'
        }}>
          <p style={{
            fontSize: '14px',
            color: colors.grayDark,
            margin: 0,
            lineHeight: '1.6'
          }}>
            {person.status === 'green' 
              ? 'Esta pessoa está disponível e aberta para conversas no momento.'
              : person.status === 'yellow'
              ? 'Esta pessoa pode estar disponível. Seja respeitoso ao iniciar contato.'
              : 'Esta pessoa não está disponível para conversas no momento. Respeite sua escolha.'}
          </p>
        </div>

        <div style={{
          marginTop: spacing.xl,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.md
        }}>
          <Button
            onClick={onStartChat}
            fullWidth
            disabled={person.status === 'red'}
          >
            Iniciar conversa
          </Button>

          <Button
            variant="ghost"
            onClick={onReport}
            style={{ color: colors.red }}
          >
            Reportar usuário
          </Button>
        </div>
      </div>
    </Container>
  );
};

// ============================================
// TELA 3B: MEU PERFIL (edição)
// ============================================

const MyProfileScreen = ({ user, onBack, onSave }) => {
  const [name,    setName]    = useState(user?.name    || '');
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState(null);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === (user?.name || '')) { onBack(); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({ name: trimmed });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onBack(); }, 1000);
    } catch (e) {
      setError(e.message || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Header title="Meu Perfil" onBack={onBack} />
      <div style={{ padding: spacing.lg }}>

        {/* Avatar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: spacing.xl }}>
          <div style={{
            width: '96px', height: '96px', borderRadius: '50%',
            backgroundColor: colors.blue, display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: colors.white, fontSize: '40px', fontWeight: '700'
          }}>
            {(name || user?.name || '?').charAt(0).toUpperCase()}
          </div>
          <p style={{ marginTop: spacing.sm, fontSize: '13px', color: colors.grayDark }}>
            {user?.phone || ''}
          </p>
        </div>

        {/* Campo nome */}
        <div style={{ marginBottom: spacing.lg }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: colors.grayDark, marginBottom: spacing.xs }}>
            Nome
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={50}
            placeholder="Seu nome no app"
            style={{
              width: '100%', padding: `${spacing.md} ${spacing.lg}`,
              borderRadius: '12px', border: `1.5px solid ${colors.gray}`,
              fontSize: '16px', outline: 'none', boxSizing: 'border-box',
              fontFamily: 'inherit'
            }}
          />
        </div>

        {/* Info somente leitura */}
        <div style={{ backgroundColor: colors.gray, borderRadius: '12px', padding: spacing.lg, marginBottom: spacing.lg }}>
          {[
            { label: 'Plano', value: user?.premium ? '✨ Premium' : 'Gratuito' },
            { label: 'Avaliação', value: user?.totalRatings > 0 ? `⭐ ${(user.rating || 0).toFixed(1)} (${user.totalRatings} avaliações)` : 'Sem avaliações ainda' },
            { label: 'Status', value: user?.status === 'green' ? '🟢 Disponível' : user?.status === 'yellow' ? '🟡 Talvez' : '🔴 Ocupado' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: `${spacing.sm} 0`, borderBottom: `1px solid ${colors.grayLight || '#eee'}` }}>
              <span style={{ fontSize: '14px', color: colors.grayDark }}>{item.label}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: colors.black }}>{item.value}</span>
            </div>
          ))}
        </div>

        {error && (
          <div style={{ backgroundColor: colors.redLight, color: colors.red, padding: spacing.md, borderRadius: '8px', marginBottom: spacing.md, fontSize: '14px' }}>
            {error}
          </div>
        )}

        <Button onClick={handleSave} fullWidth disabled={saving}>
          {saving ? 'Salvando…' : success ? '✓ Salvo!' : 'Salvar'}
        </Button>
      </div>
    </Container>
  );
};

// ============================================
// TELA 4: CHAT
// ============================================

const ChatScreen = ({ person, conversationId, socket, currentUserId, onSendMessage, onBack, isPremium, onUpgrade }) => {
  const [messages,       setMessages]       = useState([]);
  const [inputValue,     setInputValue]     = useState('');
  const [freeUsed,       setFreeUsed]       = useState(false);
  const [upgradeNeeded,  setUpgradeNeeded]  = useState(false);
  const [sending,        setSending]        = useState(false);
  const bottomRef = useRef(null);

  // Conecta ao Socket.io e entra na sala da conversa
  useEffect(() => {
    if (!socket || !conversationId) return;
    socket.emit('join_conversation', conversationId);

    const handleMsg = (msg) => {
      setMessages(prev => {
        if (prev.find(m => m.id === msg.id)) return prev; // dedup
        return [...prev, { ...msg, isMine: msg.senderId === currentUserId, time: new Date(msg.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }];
      });
    };
    socket.on('message', handleMsg);
    return () => { socket.emit('leave_conversation', conversationId); socket.off('message', handleMsg); };
  }, [socket, conversationId, currentUserId]);

  // Scroll automático para última mensagem
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    const content = inputValue.trim();
    if (!content || sending) return;
    setSending(true);
    setInputValue('');

    if (socket && conversationId) {
      socket.emit('send_message', { conversationId, content }, (res) => {
        setSending(false);
        if (res?.error === 'upgrade_required') { setUpgradeNeeded(true); setInputValue(content); }
        else if (!isPremium) setFreeUsed(true);
      });
    } else {
      // Fallback sem socket (modo demo)
      onSendMessage?.(content);
      setMessages(prev => [...prev, { id: Date.now(), content, isMine: true, time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }]);
      if (!isPremium) setFreeUsed(true);
      setSending(false);
    }
  };

  const blocked = !isPremium && (freeUsed || upgradeNeeded);

  return (
    <Container>
      <Header
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <span>{person?.name || 'Chat'}</span>
            {person?.abacaxi  && <span style={{ display: 'inline-block', transform: 'rotate(180deg)' }}>🍍</span>}
            {person?.arcoIris && <span>🏳️‍🌈</span>}
            {person?.status && <StatusIndicator status={person.status} size="sm" />}
          </div>
        }
        onBack={onBack}
      />

      {/* Banner free */}
      {!isPremium && !freeUsed && (
        <div style={{ padding: spacing.md, backgroundColor: colors.gold + '20', borderBottom: `1px solid ${colors.gold}40`, display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <AlertCircle size={16} color={colors.gold} />
          <span style={{ fontSize: '13px', color: colors.black, flex: 1 }}>Você pode enviar 1 mensagem grátis por conversa</span>
        </div>
      )}

      {/* Mensagens */}
      <div style={{ overflowY: 'auto', padding: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.md, minHeight: 'calc(100vh - 200px)', maxHeight: 'calc(100vh - 160px)' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: colors.grayDark, fontSize: '14px', marginTop: spacing.xxl }}>
            Nenhuma mensagem ainda. Diga olá! 👋
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} style={{ display: 'flex', justifyContent: msg.isMine ? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '75%', padding: spacing.md, borderRadius: '16px', backgroundColor: msg.isMine ? colors.blue : colors.gray, color: msg.isMine ? colors.white : colors.black }}>
              <p style={{ margin: 0, fontSize: '15px', lineHeight: '1.4' }}>{msg.content}</p>
              <p style={{ margin: 0, marginTop: spacing.xs, fontSize: '11px', opacity: 0.7, textAlign: 'right' }}>{msg.time}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ padding: spacing.md, borderTop: `1px solid ${colors.gray}`, backgroundColor: colors.white, position: 'sticky', bottom: 0 }}>
        {blocked && (
          <div style={{ padding: spacing.md, backgroundColor: colors.gold + '15', borderRadius: '14px', marginBottom: spacing.md, textAlign: 'center' }}>
            <Crown size={24} color={colors.gold} style={{ marginBottom: spacing.xs }} />
            <p style={{ fontSize: '14px', color: colors.black, margin: 0, marginBottom: spacing.sm, fontWeight: '600' }}>Mensagem grátis usada</p>
            <p style={{ fontSize: '13px', color: colors.grayDark, margin: 0, marginBottom: spacing.md }}>Assine o Premium para continuar conversando ilimitado</p>
            <Button onClick={onUpgrade} style={{ backgroundColor: colors.gold, fontSize: '14px', padding: `${spacing.sm} ${spacing.lg}` }}>
              ✨ Assinar Premium — R$ 9,90/mês
            </Button>
          </div>
        )}
        <div style={{ display: 'flex', gap: spacing.sm }}>
          <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} placeholder={blocked ? 'Assine Premium para continuar...' : 'Digite sua mensagem...'} disabled={blocked || sending}
            style={{ flex: 1, padding: spacing.md, borderRadius: '24px', border: `1px solid ${colors.gray}`, fontSize: '15px', outline: 'none', backgroundColor: colors.white }}
          />
          <button onClick={handleSend} disabled={!inputValue.trim() || blocked || sending}
            style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: colors.blue, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: (!inputValue.trim() || blocked || sending) ? 0.5 : 1 }}
          >
            <Send size={20} color={colors.white} />
          </button>
        </div>
      </div>
    </Container>
  );
};

// ============================================
// TELA 6: SISTEMA DE INDICAÇÃO
// ============================================

const ReferralScreen = ({ user, onBack, onShare }) => {
  const referralCode = user.referralCode || 'EAT' + user.id.slice(0, 6).toUpperCase();
  const referralLink = `https://eat.app/ref/${referralCode}`;

  return (
    <Container>
      <Header title="Indique e Ganhe" onBack={onBack} />

      <div style={{ padding: spacing.xl, textAlign: 'center' }}>
        <div style={{
          width: '120px',
          height: '120px',
          margin: '0 auto',
          marginBottom: spacing.xl,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${colors.blue}, ${colors.blueLight})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '64px'
        }}>
          🎁
        </div>

        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: colors.black,
          marginBottom: spacing.sm
        }}>Ganhe 1 Mês Grátis</h2>

        <p style={{
          fontSize: '16px',
          color: colors.grayDark,
          marginBottom: spacing.xl,
          lineHeight: '1.6'
        }}>
          Convide amigos para o EAT e ganhe <strong style={{ color: colors.black }}>1 mês de Premium</strong> quando eles fizerem a primeira assinatura (máximo 5 indicações)
        </p>

        {/* Código de Indicação */}
        <div style={{
          padding: spacing.lg,
          backgroundColor: colors.gray,
          borderRadius: '12px',
          marginBottom: spacing.lg
        }}>
          <p style={{
            fontSize: '13px',
            color: colors.grayDark,
            marginBottom: spacing.sm,
            fontWeight: '500'
          }}>Seu código de indicação</p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.sm
          }}>
            <span style={{
              fontSize: '32px',
              fontWeight: '700',
              color: colors.blue,
              letterSpacing: '2px'
            }}>{referralCode}</span>
            <button style={{
              padding: spacing.sm,
              backgroundColor: colors.blue,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              color: colors.white,
              fontSize: '12px',
              fontWeight: '600'
            }}>
              COPIAR
            </button>
          </div>
        </div>

        {/* Link de Indicação */}
        <div style={{
          padding: spacing.md,
          backgroundColor: colors.white,
          border: `1px solid ${colors.gray}`,
          borderRadius: '12px',
          marginBottom: spacing.xl,
          fontSize: '13px',
          color: colors.grayDark,
          wordBreak: 'break-all'
        }}>
          {referralLink}
        </div>

        <Button onClick={onShare} fullWidth style={{ marginBottom: spacing.md }}>
          Compartilhar Link
        </Button>

        {/* Estatísticas */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: spacing.md,
          marginBottom: spacing.xl
        }}>
          <div style={{
            padding: spacing.lg,
            backgroundColor: colors.gray,
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: '32px',
              fontWeight: '700',
              color: colors.blue,
              margin: 0,
              marginBottom: spacing.xs
            }}>{user.referralCount || 0}/5</p>
            <p style={{
              fontSize: '13px',
              color: colors.grayDark,
              margin: 0
            }}>Indicações feitas</p>
          </div>
          <div style={{
            padding: spacing.lg,
            backgroundColor: colors.gray,
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: '32px',
              fontWeight: '700',
              color: colors.gold,
              margin: 0,
              marginBottom: spacing.xs
            }}>{user.referralBonus || 0}</p>
            <p style={{
              fontSize: '13px',
              color: colors.grayDark,
              margin: 0
            }}>Meses ganhos</p>
          </div>
        </div>

        {/* Aviso de limite */}
        {user.referralCount >= 5 && (
          <div style={{
            padding: spacing.md,
            backgroundColor: colors.yellow + '20',
            borderRadius: '12px',
            marginBottom: spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm
          }}>
            <AlertCircle size={20} color={colors.yellow} />
            <p style={{
              fontSize: '14px',
              color: colors.black,
              margin: 0
            }}>
              Você atingiu o limite de 5 indicações
            </p>
          </div>
        )}

        {/* Regras */}
        <div style={{
          padding: spacing.lg,
          backgroundColor: colors.blue + '10',
          borderRadius: '12px',
          textAlign: 'left'
        }}>
          <h4 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: colors.black,
            marginBottom: spacing.md
          }}>Como funciona:</h4>
          <ul style={{
            fontSize: '13px',
            color: colors.grayDark,
            paddingLeft: spacing.lg,
            margin: 0,
            lineHeight: '1.8'
          }}>
            <li>Compartilhe seu código ou link</li>
            <li>Seu amigo cria conta e assina Premium</li>
            <li>Você recebe 1 mês grátis automaticamente</li>
            <li>Bônus aplicado uma única vez por indicado</li>
            <li>Máximo de 5 indicações válidas</li>
          </ul>
        </div>
      </div>
    </Container>
  );
};

// ============================================
// TELA 7: AVALIAÇÃO DE USUÁRIO (Pós-Chat)
// ============================================

const RatingScreen = ({ person, onRate, onSkip }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const handleSubmit = () => {
    if (rating > 0) {
      onRate(rating);
    }
  };

  return (
    <Container>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: spacing.xl,
        textAlign: 'center'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: colors.blue,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.white,
          fontSize: '32px',
          fontWeight: '600',
          marginBottom: spacing.lg
        }}>
          {(person.name || '?').charAt(0)}
        </div>

        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: colors.black,
          marginBottom: spacing.sm
        }}>Como foi sua conversa com {person.name.split(' ')[0]}?</h2>

        <p style={{
          fontSize: '15px',
          color: colors.grayDark,
          marginBottom: spacing.xl,
          lineHeight: '1.6'
        }}>
          Sua avaliação ajuda a manter o EAT seguro e respeitoso para todos
        </p>

        {/* Estrelas de Avaliação */}
        <div style={{
          display: 'flex',
          gap: spacing.sm,
          marginBottom: spacing.xl
        }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '48px',
                transition: 'transform 0.2s',
                transform: (hoverRating >= star || rating >= star) ? 'scale(1.1)' : 'scale(1)'
              }}
            >
              {(hoverRating >= star || rating >= star) ? '⭐' : '☆'}
            </button>
          ))}
        </div>

        <div style={{
          marginBottom: spacing.xl,
          minHeight: '24px'
        }}>
          {rating > 0 && (
            <p style={{
              fontSize: '14px',
              color: colors.grayDark,
              margin: 0
            }}>
              {rating === 5 ? 'Experiência excelente!' :
               rating === 4 ? 'Experiência boa' :
               rating === 3 ? 'Experiência ok' :
               rating === 2 ? 'Experiência ruim' :
               'Experiência muito ruim'}
            </p>
          )}
        </div>

        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.md
        }}>
          <Button
            onClick={handleSubmit}
            fullWidth
            disabled={rating === 0}
          >
            Enviar avaliação
          </Button>

          <Button
            variant="ghost"
            onClick={onSkip}
            fullWidth
          >
            Pular
          </Button>
        </div>

        <div style={{
          marginTop: spacing.xl,
          padding: spacing.md,
          backgroundColor: colors.gray,
          borderRadius: '12px',
          fontSize: '12px',
          color: colors.grayDark,
          lineHeight: '1.5'
        }}>
          <Shield size={16} style={{ verticalAlign: 'middle', marginRight: spacing.xs }} />
          Sua avaliação é anônima e ajuda a comunidade
        </div>
      </div>
    </Container>
  );
};

// ============================================
// TELA 5: CONFIGURAÇÕES
// ============================================

const SettingsScreen = ({ user, onBack, onLogout, onOpenReferral, onOpenPrivacy, onOpenTerms, onOpenPartnership, onDeleteAccount, onOpenAffiliatePanel, onToggleSymbol, onOpenMyProfile }) => {
  return (
    <Container>
      <Header title="Configurações" onBack={onBack} />

      <div style={{ padding: spacing.lg }}>
        {/* Perfil do usuário */}
        <div style={{
          padding: spacing.lg,
          backgroundColor: colors.gray,
          borderRadius: '12px',
          marginBottom: spacing.lg,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.md
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: colors.blue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.white,
            fontSize: '24px',
            fontWeight: '600'
          }}>
            {(user.name || '?').charAt(0)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: '4px' }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: colors.black,
                margin: 0
              }}>{user.name}</h3>
              {user.abacaxi && <span style={{ display: 'inline-block', transform: 'rotate(180deg)' }}>🍍</span>}
              {user.arcoIris && <span>🏳️‍🌈</span>}
            </div>
            <p style={{
              fontSize: '14px',
              color: colors.grayDark,
              margin: 0
            }}>{user.email}</p>
            {user.rating && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.xs,
                marginTop: spacing.xs
              }}>
                <span style={{ fontSize: '14px', color: colors.gold }}>
                  {'⭐'.repeat(Math.floor(user.rating))}
                </span>
                <span style={{ fontSize: '12px', color: colors.grayDark }}>
                  {user.rating.toFixed(1)} ({user.totalRatings} avaliações)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Bônus de Indicação */}
        <div style={{
          padding: spacing.lg,
          background: `linear-gradient(135deg, ${colors.blue}15, ${colors.blueLight}15)`,
          border: `2px solid ${colors.blue}`,
          borderRadius: '12px',
          marginBottom: spacing.lg,
          cursor: 'pointer'
        }} onClick={onOpenReferral}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.md }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              <span style={{ fontSize: '24px' }}>🎁</span>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: colors.black,
                margin: 0
              }}>Indique e Ganhe</h3>
            </div>
            <span style={{
              fontSize: '12px',
              padding: `${spacing.xs} ${spacing.sm}`,
              backgroundColor: colors.blue,
              color: colors.white,
              borderRadius: '12px',
              fontWeight: '600'
            }}>1 MÊS</span>
          </div>
          <p style={{
            fontSize: '14px',
            color: colors.grayDark,
            marginBottom: spacing.md,
            lineHeight: '1.6'
          }}>
            Convide amigos e ganhe <strong style={{ color: colors.black }}>1 mês grátis</strong> quando eles assinarem pela primeira vez (máximo 5 indicações)
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: spacing.sm,
            backgroundColor: colors.white,
            borderRadius: '8px'
          }}>
            <span style={{
              fontSize: '13px',
              color: colors.black,
              fontWeight: '500'
            }}>Indicações: {user.referralCount || 0}/5</span>
            <span style={{ color: colors.blue, fontSize: '14px', fontWeight: '600' }}>
              Ver detalhes →
            </span>
          </div>
        </div>

        {/* Assinatura */}
        {!user.premium && (
          <div style={{
            padding: spacing.lg,
            background: `linear-gradient(135deg, ${colors.gold}20, ${colors.goldLight}20)`,
            border: `2px solid ${colors.gold}`,
            borderRadius: '12px',
            marginBottom: spacing.lg
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md }}>
              <span style={{ fontSize: '24px' }}>✨</span>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: colors.black,
                margin: 0
              }}>Seja Premium</h3>
            </div>
            <ul style={{
              fontSize: '14px',
              color: colors.grayDark,
              marginBottom: spacing.md,
              paddingLeft: spacing.lg,
              lineHeight: '1.8'
            }}>
              <li>Conversas ilimitadas</li>
              <li>Destaque no perfil</li>
              <li>Suporte prioritário</li>
            </ul>
            <Button fullWidth style={{
              backgroundColor: colors.gold,
              borderColor: colors.gold,
              color: colors.white
            }}>
              Assinar por R$ 9,90/mês
            </Button>
          </div>
        )}

        {/* Opções */}
        <div style={{
          border: `1px solid ${colors.gray}`,
          borderRadius: '12px',
          overflow: 'hidden',
          marginBottom: spacing.lg
        }}>
          {[
            { label: 'Meu perfil', icon: <User size={20} />, action: onOpenMyProfile },
            { label: 'Painel de Afiliado 🤝', icon: '💎', action: onOpenAffiliatePanel },
            { label: 'Política de Privacidade', icon: <Shield size={20} />, action: onOpenPrivacy },
            { label: 'Termos de Uso', icon: '📄', action: onOpenTerms },
            { label: 'Termo de Parceria (Bar)', icon: '🤝', action: onOpenPartnership },
            { label: 'WhatsApp (Suporte)', icon: <MessageCircle size={20} />, action: () => window.open('https://wa.me/5531971836534?text=Ol%C3%A1%2C+preciso+de+suporte+no+EAT+App', '_blank') },
            { label: 'Símbolo do abacaxi', icon: '🍍', rotate: true, action: () => onToggleSymbol && onToggleSymbol('abacaxi'), active: user.abacaxi },
            { label: 'Bandeira arco-íris', icon: '🏳️‍🌈', action: () => onToggleSymbol && onToggleSymbol('arcoIris'), active: user.arcoIris }
          ].map((item, index, arr) => (
            <button
              key={item.label}
              onClick={item.action || undefined}
              style={{
                width: '100%',
                padding: spacing.md,
                backgroundColor: colors.white,
                border: 'none',
                borderBottom: index < arr.length - 1 ? `1px solid ${colors.gray}` : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textAlign: 'left'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                <span style={{ color: colors.blue }}>
                  {typeof item.icon === 'string' ? 
                    <span style={{ 
                      fontSize: '20px',
                      display: 'inline-block',
                      transform: item.rotate ? 'rotate(180deg)' : 'none'
                    }}>{item.icon}</span> : item.icon}
                </span>
                <span style={{
                  fontSize: '16px',
                  color: colors.black
                }}>{item.label}</span>
              </div>
              {'active' in item ? (
                <span style={{
                  width: '44px', height: '24px', borderRadius: '12px',
                  backgroundColor: item.active ? colors.green : colors.gray,
                  display: 'flex', alignItems: 'center',
                  padding: '2px', transition: 'background 0.2s',
                  flexShrink: 0
                }}>
                  <span style={{
                    width: '20px', height: '20px', borderRadius: '50%',
                    backgroundColor: colors.white,
                    transform: item.active ? 'translateX(20px)' : 'translateX(0)',
                    transition: 'transform 0.2s', display: 'block'
                  }} />
                </span>
              ) : (
                <span style={{ color: colors.grayDark }}>→</span>
              )}
            </button>
          ))}
        </div>

        {/* Zona de perigo */}
        <div style={{
          border: `1px solid ${colors.red}30`,
          borderRadius: '12px',
          overflow: 'hidden',
          marginBottom: spacing.lg
        }}>
          <div style={{ padding: spacing.sm, backgroundColor: colors.red + '08' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: colors.red, paddingLeft: spacing.sm }}>
              ZONA DE PERIGO
            </span>
          </div>
          <button
            onClick={onDeleteAccount}
            style={{
              width: '100%',
              padding: spacing.md,
              backgroundColor: colors.white,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              textAlign: 'left'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
              <span style={{ color: colors.red }}>
                <AlertCircle size={20} />
              </span>
              <div>
                <span style={{ fontSize: '16px', color: colors.red, fontWeight: '600', display: 'block' }}>
                  Excluir minha conta e dados
                </span>
                <span style={{ fontSize: '12px', color: colors.grayDark }}>
                  Direito de exclusão (LGPD Art. 18)
                </span>
              </div>
            </div>
            <span style={{ color: colors.red }}>→</span>
          </button>
        </div>

        <Button variant="ghost" onClick={onLogout} fullWidth style={{ color: colors.red }}>
          <LogOut size={20} style={{ marginRight: spacing.sm, verticalAlign: 'middle' }} />
          Sair
        </Button>
      </div>
    </Container>
  );
};

// ============================================
// TELA: AFILIAÇÃO — ONBOARDING (Pós-LGPD)
// ============================================

const AffiliateOnboardingScreen = ({ onContinue, onSkip }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    type: null,
    city: '',
    venueName: '',
    instagramHandle: '',
    followers: '',
    description: '',
  });

  const cities = ['São Paulo', 'Rio de Janeiro', 'Belo Horizonte', 'Curitiba', 'Porto Alegre', 'Outra'];

  if (step === 1) return (
    <Container>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'linear-gradient(160deg, #0A0A0F 0%, #16161F 60%, #0A0A0F 100%)' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: spacing.xl, textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: spacing.lg }}>🤝</div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#F0F0F8', marginBottom: spacing.sm, lineHeight: 1.2 }}>
            Seja um Afiliado EAT
          </h1>
          <p style={{ fontSize: '15px', color: '#8B8BA8', lineHeight: 1.7, marginBottom: spacing.xl, maxWidth: '340px' }}>
            Artistas e influencers parceiros ganham <strong style={{ color: '#D4AF37' }}>6% de comissão</strong> sobre as assinaturas geradas na sua cidade ou estabelecimento.
          </p>
          <div style={{ width: '100%', maxWidth: '340px', display: 'flex', flexDirection: 'column', gap: spacing.md, marginBottom: spacing.xl }}>
            {[
              { icon: '🎵', title: 'Artista Embaixador', desc: 'Exclusivo de uma cidade. Você representa o EAT em toda a cena local.' },
              { icon: '⭐', title: 'Influencer Embaixador', desc: 'Vinculado a uma ou mais casas noturnas específicas.' },
            ].map(item => (
              <div key={item.title} style={{ padding: spacing.md, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', textAlign: 'left', display: 'flex', gap: spacing.md }}>
                <span style={{ fontSize: '28px', flexShrink: 0 }}>{item.icon}</span>
                <div>
                  <div style={{ fontWeight: '700', color: '#F0F0F8', marginBottom: '4px' }}>{item.title}</div>
                  <div style={{ fontSize: '13px', color: '#8B8BA8', lineHeight: 1.5 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setStep(2)} style={{ width: '100%', maxWidth: '340px', padding: `${spacing.md} ${spacing.lg}`, background: 'linear-gradient(135deg, #D4AF37, #8B7020)', color: '#000', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', marginBottom: spacing.md }}>
            Quero ser afiliado →
          </button>
          <button onClick={onSkip} style={{ background: 'none', border: 'none', color: '#4A4A6A', fontSize: '14px', cursor: 'pointer', padding: spacing.sm }}>
            Pular por enquanto
          </button>
        </div>
      </div>
    </Container>
  );

  if (step === 2) return (
    <Container>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#0A0A0F', padding: spacing.xl }}>
        <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: '#8B8BA8', fontSize: '24px', cursor: 'pointer', alignSelf: 'flex-start', marginBottom: spacing.xl }}>←</button>
        <div style={{ marginBottom: spacing.xl }}>
          <div style={{ fontSize: '12px', color: '#D4AF37', fontWeight: '700', letterSpacing: '1px', marginBottom: spacing.sm }}>PASSO 1 DE 3</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#F0F0F8', marginBottom: spacing.sm }}>Qual é o seu perfil?</h2>
          <p style={{ fontSize: '14px', color: '#8B8BA8', lineHeight: 1.6 }}>Isso define como sua comissão será calculada.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, flex: 1 }}>
          {[
            { type: 'artist', icon: '🎵', title: 'Artista / DJ / Banda', desc: 'Você representa uma cidade inteira. Sua comissão é baseada na receita total de assinaturas geradas nessa cidade.', color: '#8B5CF6' },
            { type: 'influencer', icon: '⭐', title: 'Influencer / Creator', desc: 'Você se vincula a casas noturnas específicas. Sua comissão vem das assinaturas geradas nesses estabelecimentos.', color: '#D4AF37' },
          ].map(opt => (
            <div key={opt.type} onClick={() => setForm({ ...form, type: opt.type })} style={{
              padding: spacing.lg,
              background: form.type === opt.type ? opt.color + '15' : 'rgba(255,255,255,0.03)',
              border: '2px solid ' + (form.type === opt.type ? opt.color : 'rgba(255,255,255,0.08)'),
              borderRadius: '16px', cursor: 'pointer', transition: 'all 0.2s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.sm }}>
                <span style={{ fontSize: '32px' }}>{opt.icon}</span>
                <span style={{ fontWeight: '700', fontSize: '17px', color: form.type === opt.type ? opt.color : '#F0F0F8' }}>{opt.title}</span>
                {form.type === opt.type && <span style={{ marginLeft: 'auto', fontSize: '20px', color: opt.color }}>✓</span>}
              </div>
              <p style={{ fontSize: '13px', color: '#8B8BA8', lineHeight: 1.6, margin: 0 }}>{opt.desc}</p>
            </div>
          ))}
        </div>
        <button onClick={() => form.type && setStep(3)} disabled={!form.type} style={{
          marginTop: spacing.xl, padding: `${spacing.md} ${spacing.lg}`,
          background: form.type ? 'linear-gradient(135deg, #D4AF37, #8B7020)' : '#1E1E2E',
          color: form.type ? '#000' : '#4A4A6A',
          border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700',
          cursor: form.type ? 'pointer' : 'not-allowed',
        }}>Continuar →</button>
      </div>
    </Container>
  );

  if (step === 3) return (
    <Container>
      <div style={{ minHeight: '100vh', background: '#0A0A0F', padding: spacing.xl }}>
        <button onClick={() => setStep(2)} style={{ background: 'none', border: 'none', color: '#8B8BA8', fontSize: '24px', cursor: 'pointer', marginBottom: spacing.xl, display: 'block' }}>←</button>
        <div style={{ marginBottom: spacing.xl }}>
          <div style={{ fontSize: '12px', color: '#D4AF37', fontWeight: '700', letterSpacing: '1px', marginBottom: spacing.sm }}>PASSO 2 DE 3</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#F0F0F8', marginBottom: spacing.sm }}>
            {form.type === 'artist' ? '🎵 Dados do Artista' : '⭐ Dados do Influencer'}
          </h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <div>
            <label style={{ fontSize: '12px', color: '#8B8BA8', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
              {form.type === 'artist' ? 'Nome artístico / Banda' : 'Handle do Instagram'}
            </label>
            <input value={form.instagramHandle} onChange={e => setForm({ ...form, instagramHandle: e.target.value })}
              placeholder={form.type === 'artist' ? 'Ex: DJ Fever' : '@seu.perfil'}
              style={{ width: '100%', background: '#16161F', color: '#F0F0F8', border: '1px solid #1E1E2E', borderRadius: '12px', padding: spacing.md, fontSize: '15px', outline: 'none' }} />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#8B8BA8', display: 'block', marginBottom: '6px', fontWeight: '600' }}>Seguidores no Instagram</label>
            <input value={form.followers} onChange={e => setForm({ ...form, followers: e.target.value })}
              placeholder="Ex: 50000"
              style={{ width: '100%', background: '#16161F', color: '#F0F0F8', border: '1px solid #1E1E2E', borderRadius: '12px', padding: spacing.md, fontSize: '15px', outline: 'none' }} />
          </div>
          {form.type === 'artist' ? (
            <div>
              <label style={{ fontSize: '12px', color: '#8B8BA8', display: 'block', marginBottom: '6px', fontWeight: '600' }}>Cidade de atuação</label>
              <select value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                style={{ width: '100%', background: '#16161F', color: '#F0F0F8', border: '1px solid #1E1E2E', borderRadius: '12px', padding: spacing.md, fontSize: '15px', outline: 'none' }}>
                <option value="">Selecione sua cidade...</option>
                {cities.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label style={{ fontSize: '12px', color: '#8B8BA8', display: 'block', marginBottom: '6px', fontWeight: '600' }}>Nome da(s) casa(s) noturna(s)</label>
              <input value={form.venueName} onChange={e => setForm({ ...form, venueName: e.target.value })}
                placeholder="Ex: Club Noir, Lux Underground..."
                style={{ width: '100%', background: '#16161F', color: '#F0F0F8', border: '1px solid #1E1E2E', borderRadius: '12px', padding: spacing.md, fontSize: '15px', outline: 'none' }} />
              <p style={{ fontSize: '12px', color: '#4A4A6A', marginTop: '6px' }}>Você pode adicionar mais estabelecimentos depois.</p>
            </div>
          )}
          <div>
            <label style={{ fontSize: '12px', color: '#8B8BA8', display: 'block', marginBottom: '6px', fontWeight: '600' }}>Breve apresentação</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Fale sobre você e sua relação com a cena noturna..."
              rows={3}
              style={{ width: '100%', background: '#16161F', color: '#F0F0F8', border: '1px solid #1E1E2E', borderRadius: '12px', padding: spacing.md, fontSize: '15px', outline: 'none', resize: 'none', fontFamily: 'inherit' }} />
          </div>
        </div>
        <button onClick={() => {
          const valid = form.type === 'artist' ? (form.instagramHandle && form.city) : (form.instagramHandle && form.venueName);
          if (valid) setStep(4); else alert('Preencha os campos obrigatórios.');
        }} style={{ marginTop: spacing.xl, width: '100%', padding: `${spacing.md} ${spacing.lg}`, background: 'linear-gradient(135deg, #D4AF37, #8B7020)', color: '#000', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700', cursor: 'pointer' }}>
          Revisar candidatura →
        </button>
      </div>
    </Container>
  );

  return (
    <Container>
      <div style={{ minHeight: '100vh', background: '#0A0A0F', padding: spacing.xl }}>
        <button onClick={() => setStep(3)} style={{ background: 'none', border: 'none', color: '#8B8BA8', fontSize: '24px', cursor: 'pointer', marginBottom: spacing.xl, display: 'block' }}>←</button>
        <div style={{ marginBottom: spacing.xl }}>
          <div style={{ fontSize: '12px', color: '#D4AF37', fontWeight: '700', letterSpacing: '1px', marginBottom: spacing.sm }}>PASSO 3 DE 3</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#F0F0F8', marginBottom: spacing.sm }}>Confirmar candidatura</h2>
        </div>
        <div style={{ background: '#16161F', border: '1px solid #1E1E2E', borderRadius: '16px', padding: spacing.lg, marginBottom: spacing.lg }}>
          {[
            { label: 'Tipo', value: form.type === 'artist' ? '🎵 Artista Embaixador' : '⭐ Influencer Embaixador' },
            { label: form.type === 'artist' ? 'Artista' : 'Handle', value: form.instagramHandle },
            { label: form.type === 'artist' ? 'Cidade' : 'Estabelecimento(s)', value: form.type === 'artist' ? form.city : form.venueName },
            { label: 'Seguidores', value: parseInt(form.followers || 0).toLocaleString('pt-BR') },
          ].map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: spacing.sm, marginBottom: spacing.sm, borderBottom: '1px solid #1E1E2E' }}>
              <span style={{ fontSize: '13px', color: '#8B8BA8' }}>{row.label}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#F0F0F8' }}>{row.value}</span>
            </div>
          ))}
          <div style={{ padding: spacing.md, background: '#D4AF3715', borderRadius: '10px', marginTop: spacing.sm }}>
            <div style={{ fontSize: '12px', color: '#8B8BA8', marginBottom: '4px' }}>Comissão após aprovação</div>
            <div style={{ fontSize: '18px', fontWeight: '800', color: '#D4AF37' }}>6% da receita líquida</div>
          </div>
        </div>
        <div style={{ padding: spacing.md, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '12px', marginBottom: spacing.xl, fontSize: '13px', color: '#8B8BA8', lineHeight: 1.6 }}>
          ⚡ Sua candidatura será analisada pela equipe EAT em até 48h. Você receberá uma notificação com o resultado.
        </div>
        <button onClick={() => onContinue({ ...form })} style={{ width: '100%', padding: `${spacing.md} ${spacing.lg}`, background: 'linear-gradient(135deg, #D4AF37, #8B7020)', color: '#000', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', marginBottom: spacing.md }}>
          Enviar candidatura ✓
        </button>
        <button onClick={onSkip} style={{ width: '100%', background: 'none', border: 'none', color: '#4A4A6A', fontSize: '14px', cursor: 'pointer', padding: spacing.sm }}>
          Fazer isso depois
        </button>
      </div>
    </Container>
  );
};

// ============================================
// TELA: PAINEL DO AFILIADO (Pós-aprovação)
// ============================================

const AffiliatePanelScreen = ({ affiliate, onBack }) => {
  const aff = affiliate || {
    type: 'influencer',
    status: 'active',
    name: '@nightlife.sp',
    city: 'São Paulo',
    venueName: 'Club Noir, Lux Underground',
    monthlyEarnings: 1548.72,
    usersReferred: 312,
    affiliateCode: 'EAT-NGTSP-7X2',
    history: [620, 840, 1100, 1290, 1420, 1548],
    pendingPayout: 1548.72,
    nextPayoutDate: '05/03/2026',
  };

  const statusConfig = ({
    active:  { label: 'Ativo',      color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
    pending: { label: 'Em análise', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
    inactive:{ label: 'Inativo',    color: '#EF4444', bg: 'rgba(239,68,68,0.12)'  },
  })[aff.status];

  const affiliateLink = 'https://eat.app/a/' + aff.affiliateCode;
  const maxBar = Math.max(...aff.history, 1);
  const months = ['Set','Out','Nov','Dez','Jan','Fev'];

  const Metric = ({ icon, label, value, sub, accent }) => (
    <div style={{ background: '#16161F', border: '1px solid #1E1E2E', borderRadius: '14px', padding: spacing.md, textAlign: 'center' }}>
      <div style={{ fontSize: '24px', marginBottom: '6px' }}>{icon}</div>
      <div style={{ fontSize: '22px', fontWeight: '800', color: accent || '#F0F0F8' }}>{value}</div>
      <div style={{ fontSize: '12px', color: '#8B8BA8', marginTop: '2px' }}>{label}</div>
      {sub && <div style={{ fontSize: '11px', color: '#4A4A6A', marginTop: '2px' }}>{sub}</div>}
    </div>
  );

  return (
    <Container style={{ background: '#0A0A0F' }}>
      <div style={{ background: '#111118', borderBottom: '1px solid #1E1E2E', padding: spacing.md, display: 'flex', alignItems: 'center', gap: spacing.md, position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#8B8BA8' }}>←</button>
        <div>
          <div style={{ fontWeight: '700', fontSize: '16px', color: '#F0F0F8' }}>Painel do Afiliado</div>
          <div style={{ fontSize: '12px', color: '#8B8BA8' }}>{aff.type === 'artist' ? '🎵 Artista Embaixador' : '⭐ Influencer Embaixador'}</div>
        </div>
        <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', background: statusConfig.bg, color: statusConfig.color }}>
          ● {statusConfig.label}
        </span>
      </div>

      <div style={{ padding: spacing.lg, paddingBottom: spacing.xxl }}>
        {aff.status === 'pending' && (
          <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '14px', padding: spacing.lg, marginBottom: spacing.lg, textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: spacing.sm }}>⏳</div>
            <div style={{ fontWeight: '700', color: '#F0F0F8', marginBottom: spacing.xs }}>Candidatura em análise</div>
            <div style={{ fontSize: '13px', color: '#8B8BA8', lineHeight: 1.6 }}>Nossa equipe analisará em até 48h. Você será notificado por e-mail.</div>
          </div>
        )}

        <div style={{ background: '#16161F', border: '1px solid #1E1E2E', borderRadius: '14px', padding: spacing.lg, marginBottom: spacing.lg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.md }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'linear-gradient(135deg, #D4AF3733, #8B5CF633)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', border: '2px solid #D4AF3744' }}>
              {aff.type === 'artist' ? '🎵' : '⭐'}
            </div>
            <div>
              <div style={{ fontWeight: '700', fontSize: '16px', color: '#F0F0F8' }}>{aff.name}</div>
              <div style={{ fontSize: '13px', color: '#8B8BA8', marginTop: '2px' }}>
                {aff.type === 'artist' ? '📍 ' + aff.city : '🏠 ' + aff.venueName}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <div style={{ flex: 1, background: '#0A0A0F', borderRadius: '10px', padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#4A4A6A', marginBottom: '2px' }}>Código</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#D4AF37', letterSpacing: '1px' }}>{aff.affiliateCode}</div>
            </div>
            <div style={{ flex: 1, background: '#0A0A0F', borderRadius: '10px', padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#4A4A6A', marginBottom: '2px' }}>Comissão</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#10B981' }}>6% líquido</div>
            </div>
          </div>
        </div>

        {aff.status === 'active' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md, marginBottom: spacing.lg }}>
              <Metric icon="👥" label="Usuários trazidos" value={aff.usersReferred} sub="via seu link" accent="#8B5CF6" />
              <Metric icon="💰" label="Comissão acumulada" value={'R$ ' + aff.monthlyEarnings.toFixed(2).replace('.',',')} accent="#D4AF37" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md, marginBottom: spacing.lg }}>
              <Metric icon="⏳" label="A receber" value={'R$ ' + aff.pendingPayout.toFixed(2).replace('.',',')} accent="#10B981" />
              <Metric icon="📅" label="Próximo pagamento" value={aff.nextPayoutDate} sub="5º dia útil" />
            </div>

            <div style={{ background: '#16161F', border: '1px solid #1E1E2E', borderRadius: '14px', padding: spacing.lg, marginBottom: spacing.lg }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#8B8BA8', marginBottom: spacing.md, textTransform: 'uppercase', letterSpacing: '1px' }}>📈 Histórico de comissões</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '60px', marginBottom: spacing.sm }}>
                {aff.history.map((v, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                    <div style={{ width: '100%', height: Math.max((v / maxBar) * 100, v > 0 ? 5 : 0) + '%', background: i === aff.history.length - 1 ? '#D4AF37' : '#D4AF3744', borderRadius: '4px 4px 0 0' }} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {months.map(m => <span key={m} style={{ fontSize: '10px', color: '#4A4A6A' }}>{m}</span>)}
              </div>
            </div>

            <div style={{ background: '#16161F', border: '1px solid #1E1E2E', borderRadius: '14px', padding: spacing.lg, marginBottom: spacing.lg }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#8B8BA8', marginBottom: spacing.md, textTransform: 'uppercase', letterSpacing: '1px' }}>🔗 Link e QR exclusivo</div>
              <div style={{ background: '#0A0A0F', borderRadius: '10px', padding: '12px 14px', marginBottom: spacing.md, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm }}>
                <span style={{ fontSize: '12px', color: '#8B8BA8', wordBreak: 'break-all', flex: 1 }}>{affiliateLink}</span>
                <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(affiliateLink)} style={{ background: '#D4AF37', border: 'none', borderRadius: '8px', padding: '6px 12px', color: '#000', fontSize: '11px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
                  COPIAR
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: spacing.lg, background: '#fff', borderRadius: '12px', marginBottom: spacing.md }}>
                <div style={{ width: '120px', height: '120px', position: 'relative' }}>
                  <svg viewBox="0 0 21 21" width="120" height="120" xmlns="http://www.w3.org/2000/svg">
                    <rect width="21" height="21" fill="white"/>
                    <rect x="0" y="0" width="7" height="7" fill="#1A1A1A"/>
                    <rect x="1" y="1" width="5" height="5" fill="white"/>
                    <rect x="2" y="2" width="3" height="3" fill="#1A1A1A"/>
                    <rect x="14" y="0" width="7" height="7" fill="#1A1A1A"/>
                    <rect x="15" y="1" width="5" height="5" fill="white"/>
                    <rect x="16" y="2" width="3" height="3" fill="#1A1A1A"/>
                    <rect x="0" y="14" width="7" height="7" fill="#1A1A1A"/>
                    <rect x="1" y="15" width="5" height="5" fill="white"/>
                    <rect x="2" y="16" width="3" height="3" fill="#1A1A1A"/>
                    <rect x="9" y="0" width="1" height="1" fill="#1A1A1A"/>
                    <rect x="11" y="0" width="1" height="1" fill="#1A1A1A"/>
                    <rect x="9" y="2" width="2" height="1" fill="#1A1A1A"/>
                    <rect x="9" y="4" width="1" height="2" fill="#1A1A1A"/>
                    <rect x="11" y="3" width="2" height="1" fill="#1A1A1A"/>
                    <rect x="0" y="9" width="2" height="1" fill="#1A1A1A"/>
                    <rect x="4" y="9" width="1" height="2" fill="#1A1A1A"/>
                    <rect x="6" y="10" width="2" height="1" fill="#1A1A1A"/>
                    <rect x="9" y="9" width="3" height="1" fill="#1A1A1A"/>
                    <rect x="13" y="9" width="2" height="1" fill="#1A1A1A"/>
                    <rect x="16" y="9" width="5" height="1" fill="#1A1A1A"/>
                    <rect x="10" y="11" width="1" height="3" fill="#1A1A1A"/>
                    <rect x="14" y="11" width="2" height="1" fill="#1A1A1A"/>
                    <rect x="18" y="11" width="3" height="2" fill="#1A1A1A"/>
                    <rect x="9" y="14" width="2" height="2" fill="#1A1A1A"/>
                    <rect x="12" y="14" width="1" height="3" fill="#1A1A1A"/>
                    <rect x="14" y="15" width="3" height="1" fill="#1A1A1A"/>
                    <rect x="19" y="14" width="2" height="1" fill="#1A1A1A"/>
                    <rect x="9" y="17" width="2" height="1" fill="#1A1A1A"/>
                    <rect x="16" y="17" width="1" height="3" fill="#1A1A1A"/>
                    <rect x="18" y="18" width="3" height="2" fill="#1A1A1A"/>
                  </svg>
                </div>
                <div style={{ fontSize: '11px', color: '#6B7280', marginTop: spacing.sm, fontWeight: '700', letterSpacing: '1px' }}>{aff.affiliateCode}</div>
              </div>
              <button style={{ width: '100%', padding: spacing.md, background: 'linear-gradient(135deg, #D4AF37, #8B7020)', color: '#000', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' }}>
                Compartilhar link e QR ↗
              </button>
            </div>
          </>
        )}

        <div style={{ background: '#16161F', border: '1px solid #1E1E2E', borderRadius: '14px', padding: spacing.lg }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#8B8BA8', marginBottom: spacing.md, textTransform: 'uppercase', letterSpacing: '1px' }}>💡 Como funciona</div>
          {[
            { n: '1', text: 'Compartilhe seu link ou QR com seu público' },
            { n: '2', text: 'Seu seguidor baixa o app e faz check-in no local' },
            { n: '3', text: 'Quando ele assinar Premium, você recebe 6% do valor líquido' },
            { n: '4', text: 'Pagamento todo mês até o 5º dia útil' },
          ].map(item => (
            <div key={item.n} style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.md, marginBottom: spacing.md }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#D4AF37', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800', color: '#000', flexShrink: 0 }}>{item.n}</div>
              <span style={{ fontSize: '14px', color: '#8B8BA8', lineHeight: 1.5 }}>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
};

// ============================================
// TELA: CONSENTIMENTO LGPD (Onboarding)
// ============================================

const ConsentScreen = ({ onAccept, onDecline }) => {
  const [checkedAge, setCheckedAge] = useState(false);
  const [checkedPrivacy, setCheckedPrivacy] = useState(false);
  const [checkedTerms, setCheckedTerms] = useState(false);
  const [checkedLocation, setCheckedLocation] = useState(false);

  const allChecked = checkedAge && checkedPrivacy && checkedTerms && checkedLocation;

  const CheckItem = ({ checked, onChange, children }) => (
    <div
      onClick={onChange}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing.md,
        padding: spacing.md,
        backgroundColor: checked ? colors.blue + '10' : colors.gray,
        borderRadius: '12px',
        cursor: 'pointer',
        border: `2px solid ${checked ? colors.blue : 'transparent'}`,
        transition: 'all 0.2s',
        marginBottom: spacing.sm
      }}
    >
      <div style={{
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        border: `2px solid ${checked ? colors.blue : colors.grayDark}`,
        backgroundColor: checked ? colors.blue : colors.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: '2px',
        transition: 'all 0.2s'
      }}>
        {checked && <span style={{ color: colors.white, fontSize: '14px', fontWeight: '700' }}>✓</span>}
      </div>
      <span style={{ fontSize: '14px', color: colors.black, lineHeight: '1.5' }}>{children}</span>
    </div>
  );

  return (
    <Container>
      <div style={{ padding: spacing.lg, paddingBottom: '100px' }}>
        <div style={{ textAlign: 'center', marginBottom: spacing.xl, paddingTop: spacing.lg }}>
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '18px',
            backgroundColor: colors.blue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            marginBottom: spacing.md
          }}>
            <Shield size={36} color={colors.white} />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: colors.black, marginBottom: spacing.sm }}>
            Antes de começar
          </h1>
          <p style={{ fontSize: '15px', color: colors.grayDark, lineHeight: '1.6' }}>
            O EAT respeita sua privacidade. Leia e aceite os termos para continuar.
          </p>
        </div>

        <div style={{
          padding: spacing.md,
          backgroundColor: colors.red + '10',
          borderRadius: '12px',
          marginBottom: spacing.lg,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm
        }}>
          <AlertCircle size={18} color={colors.red} />
          <span style={{ fontSize: '13px', color: colors.black, fontWeight: '600' }}>
            Aplicativo exclusivo para maiores de 18 anos (LGPD Art. 14)
          </span>
        </div>

        <CheckItem checked={checkedAge} onChange={() => setCheckedAge(!checkedAge)}>
          <strong>Confirmo que tenho 18 anos ou mais</strong> e estou ciente de que o EAT é um aplicativo de interação social para adultos.
        </CheckItem>

        <CheckItem checked={checkedPrivacy} onChange={() => setCheckedPrivacy(!checkedPrivacy)}>
          Li e aceito a <strong style={{ color: colors.blue }}>Política de Privacidade</strong>, incluindo o tratamento de dados sensíveis como minha localização no estabelecimento e status social, conforme a LGPD.
        </CheckItem>

        <CheckItem checked={checkedTerms} onChange={() => setCheckedTerms(!checkedTerms)}>
          Li e aceito os <strong style={{ color: colors.blue }}>Termos de Uso</strong>, incluindo as regras de conduta, sistema de banimento e isenção de responsabilidade do app por interações presenciais.
        </CheckItem>

        <CheckItem checked={checkedLocation} onChange={() => setCheckedLocation(!checkedLocation)}>
          Autorizo o uso da minha <strong>localização</strong> apenas enquanto estou no estabelecimento com check-in ativo. Posso revogar este consentimento a qualquer momento.
        </CheckItem>

        <div style={{
          padding: spacing.md,
          backgroundColor: colors.gray,
          borderRadius: '12px',
          marginBottom: spacing.lg,
          fontSize: '12px',
          color: colors.grayDark,
          lineHeight: '1.6'
        }}>
          <strong style={{ color: colors.black, display: 'block', marginBottom: spacing.xs }}>Seus direitos (LGPD):</strong>
          Você pode solicitar acesso, correção ou exclusão dos seus dados a qualquer momento em Configurações → Privacidade ou pelo e-mail <strong>privacidade@eat.app</strong>
        </div>

        <Button onClick={onAccept} fullWidth disabled={!allChecked} style={{ marginBottom: spacing.sm }}>
          {allChecked ? 'Continuar →' : `Aceite todos os ${4 - [checkedAge, checkedPrivacy, checkedTerms, checkedLocation].filter(Boolean).length} item(s) restante(s)`}
        </Button>

        <Button variant="ghost" onClick={onDecline} fullWidth style={{ color: colors.grayDark, fontSize: '14px' }}>
          Recusar e sair
        </Button>
      </div>
    </Container>
  );
};

// ============================================
// TELA: POLÍTICA DE PRIVACIDADE
// ============================================

const PrivacyPolicyScreen = ({ onBack }) => {
  const Section = ({ title, children }) => (
    <div style={{ marginBottom: spacing.xl }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', color: colors.black, marginBottom: spacing.sm, borderLeft: `4px solid ${colors.blue}`, paddingLeft: spacing.sm }}>
        {title}
      </h3>
      <div style={{ fontSize: '14px', color: colors.grayDark, lineHeight: '1.8' }}>
        {children}
      </div>
    </div>
  );

  return (
    <Container>
      <Header title="Política de Privacidade" onBack={onBack} />
      <div style={{ padding: spacing.lg, paddingBottom: spacing.xxl }}>
        <p style={{ fontSize: '12px', color: colors.grayDark, marginBottom: spacing.lg }}>
          Última atualização: 01/01/2025 · Versão 1.0
        </p>

        <Section title="1. Dados coletados">
          <p>Coletamos os seguintes dados pessoais para funcionamento do app:</p>
          <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
            <li><strong>Identificação:</strong> número de telefone ou e-mail para autenticação</li>
            <li><strong>Localização:</strong> presença no estabelecimento via QR Code (não rastreamento GPS contínuo)</li>
            <li><strong>Status social:</strong> disponibilidade que você define voluntariamente</li>
            <li><strong>Preferências:</strong> símbolos de perfil (abacaxi, arco-íris) que você escolhe exibir</li>
            <li><strong>Interações:</strong> logs de conversa para fins de segurança e moderação</li>
            <li><strong>Dispositivo:</strong> ID único do dispositivo para prevenção de fraudes e banimentos</li>
          </ul>
        </Section>

        <Section title="2. Dados sensíveis (LGPD Art. 11)">
          <p>Os seguintes dados são considerados sensíveis e tratados com proteção reforçada:</p>
          <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
            <li>Preferências de interação social (símbolos de perfil)</li>
            <li>Localização em tempo real no estabelecimento</li>
          </ul>
          <p style={{ marginTop: spacing.sm }}>Base legal: <strong>consentimento explícito</strong> do titular (Art. 11, I da LGPD).</p>
        </Section>

        <Section title="3. Finalidade do tratamento">
          <p>Usamos seus dados exclusivamente para:</p>
          <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
            <li>Facilitar conexões entre pessoas no mesmo estabelecimento</li>
            <li>Garantir segurança e moderação da plataforma</li>
            <li>Cumprir obrigações legais</li>
            <li>Prevenir abusos e fraudes</li>
          </ul>
          <p style={{ marginTop: spacing.sm, color: colors.red, fontWeight: '600' }}>
            NÃO vendemos nem compartilhamos seus dados com terceiros para fins comerciais.
          </p>
        </Section>

        <Section title="4. Compartilhamento de dados">
          <p>Seus dados podem ser compartilhados apenas com:</p>
          <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
            <li>Autoridades competentes quando exigido por lei</li>
            <li>Provedores de infraestrutura (hospedagem, banco de dados) sob acordos de confidencialidade</li>
          </ul>
          <p style={{ marginTop: spacing.sm }}>Os estabelecimentos parceiros <strong>NÃO têm acesso</strong> a seus dados pessoais.</p>
        </Section>

        <Section title="5. Retenção de dados">
          <ul style={{ paddingLeft: spacing.lg }}>
            <li>Dados de localização: excluídos ao final do check-out</li>
            <li>Logs de interação: 12 meses para fins de segurança</li>
            <li>Dados de conta: até você solicitar exclusão</li>
            <li>Logs de denúncia: 5 anos (obrigação legal)</li>
          </ul>
        </Section>

        <Section title="6. Seus direitos (LGPD Art. 18)">
          <p>Você tem direito a:</p>
          <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
            <li><strong>Acesso:</strong> saber quais dados temos sobre você</li>
            <li><strong>Correção:</strong> corrigir dados incompletos ou incorretos</li>
            <li><strong>Exclusão:</strong> apagar seus dados (exceto os exigidos por lei)</li>
            <li><strong>Portabilidade:</strong> receber seus dados em formato legível</li>
            <li><strong>Revogação:</strong> retirar seu consentimento a qualquer momento</li>
            <li><strong>Oposição:</strong> opor-se ao tratamento em determinadas situações</li>
          </ul>
          <p style={{ marginTop: spacing.sm }}>Exercite seus direitos em: <strong style={{ color: colors.blue }}>privacidade@eat.app</strong></p>
        </Section>

        <Section title="7. Segurança">
          <p>Adotamos medidas técnicas e organizacionais para proteger seus dados, incluindo criptografia em trânsito (TLS) e em repouso, controle de acesso e monitoramento contínuo.</p>
        </Section>

        <Section title="8. Contato do DPO">
          <p>Encarregado de Proteção de Dados (DPO):<br/>
          <strong>dpo@eat.app</strong><br/>
          EAT Tecnologia Ltda · CNPJ: XX.XXX.XXX/0001-XX</p>
        </Section>
      </div>
    </Container>
  );
};

// ============================================
// TELA: TERMOS DE USO
// ============================================

const TermsOfUseScreen = ({ onBack }) => {
  const Section = ({ title, children, highlight }) => (
    <div style={{ marginBottom: spacing.xl }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', color: colors.black, marginBottom: spacing.sm, borderLeft: `4px solid ${highlight || colors.blue}`, paddingLeft: spacing.sm }}>
        {title}
      </h3>
      <div style={{ fontSize: '14px', color: colors.grayDark, lineHeight: '1.8' }}>
        {children}
      </div>
    </div>
  );

  return (
    <Container>
      <Header title="Termos de Uso" onBack={onBack} />
      <div style={{ padding: spacing.lg, paddingBottom: spacing.xxl }}>
        <p style={{ fontSize: '12px', color: colors.grayDark, marginBottom: spacing.lg }}>
          Última atualização: 01/01/2025 · Versão 1.0
        </p>

        <Section title="1. Natureza do serviço" highlight={colors.blue}>
          <p>O EAT é uma <strong>plataforma de facilitação de interações sociais</strong> em estabelecimentos parceiros. O aplicativo:</p>
          <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
            <li><strong>Não garante</strong> a realização de encontros, conversas ou qualquer tipo de interação</li>
            <li><strong>Não é responsável</strong> pelo comportamento de outros usuários</li>
            <li><strong>Não substitui</strong> o julgamento pessoal do usuário em interações presenciais</li>
          </ul>
        </Section>

        <Section title="2. Elegibilidade" highlight={colors.blue}>
          <ul style={{ paddingLeft: spacing.lg }}>
            <li>Ter no mínimo <strong>18 anos de idade</strong></li>
            <li>Ter capacidade legal para celebrar contratos</li>
            <li>Não ter sido banido anteriormente da plataforma</li>
            <li>Residir no Brasil</li>
          </ul>
        </Section>

        <Section title="3. Código de conduta obrigatório" highlight={colors.red}>
          <p><strong>É expressamente proibido:</strong></p>
          <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
            <li>Assédio sexual, moral ou de qualquer natureza</li>
            <li>Comportamento intimidatório, ameaçador ou agressivo</li>
            <li>Envio de conteúdo pornográfico, violento ou ofensivo</li>
            <li>Perseguição (stalking) a outros usuários</li>
            <li>Criação de perfis falsos ou uso de identidade de terceiros</li>
            <li>Uso do app para fins comerciais não autorizados</li>
            <li>Tentativa de contornar sistemas de segurança ou banimento</li>
            <li>Discriminação por raça, gênero, orientação sexual, religião ou qualquer outro motivo</li>
          </ul>
          <p style={{ marginTop: spacing.sm, fontWeight: '600', color: colors.red }}>
            Violações resultam em banimento imediato e permanente.
          </p>
        </Section>

        <Section title="4. Sistema de moderação e banimento" highlight={colors.red}>
          <p>O EAT adota um sistema progressivo de moderação:</p>
          <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
            <li><strong>Aviso:</strong> para infrações leves (1ª ocorrência)</li>
            <li><strong>Suspensão temporária:</strong> 7 a 30 dias para infrações moderadas</li>
            <li><strong>Banimento permanente:</strong> para infrações graves ou reincidência</li>
          </ul>
          <p style={{ marginTop: spacing.sm }}>Banimentos são vinculados ao dispositivo (Device ID) e número de telefone, impedindo recriação de contas.</p>
        </Section>

        <Section title="5. Responsabilidade do usuário" highlight={colors.yellow}>
          <p>O usuário é <strong>integral e exclusivamente responsável</strong> por:</p>
          <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
            <li>Sua conduta no aplicativo e no estabelecimento</li>
            <li>Todas as mensagens enviadas através da plataforma</li>
            <li>Interações presenciais decorrentes do uso do app</li>
            <li>Consequências legais de seu comportamento</li>
          </ul>
        </Section>

        <Section title="6. Isenção de responsabilidade por interações presenciais" highlight={colors.yellow}>
          <div style={{ padding: spacing.md, backgroundColor: colors.yellow + '15', borderRadius: '8px', border: `1px solid ${colors.yellow}` }}>
            <p><strong>O EAT não é responsável por quaisquer danos, prejuízos ou consequências</strong> decorrentes de interações presenciais entre usuários, incluindo mas não limitado a: lesões corporais, danos morais, danos materiais, crimes ou contravenções praticadas entre usuários.</p>
          </div>
        </Section>

        <Section title="7. Propriedade intelectual" highlight={colors.blue}>
          <p>Todo o conteúdo do aplicativo (design, código, marca) é de propriedade do EAT. Mensagens enviadas pelos usuários permanecem de sua autoria, sendo concedida ao EAT licença limitada para moderação e segurança.</p>
        </Section>

        <Section title="8. Alterações nos termos" highlight={colors.blue}>
          <p>O EAT pode atualizar estes termos a qualquer momento. Alterações significativas serão comunicadas com 15 dias de antecedência. O uso continuado após comunicação implica aceitação dos novos termos.</p>
        </Section>

        <Section title="9. Foro e legislação" highlight={colors.blue}>
          <p>Estes termos são regidos pelas leis brasileiras. Fica eleito o foro da Comarca de São Paulo - SP para dirimir eventuais conflitos.</p>
        </Section>
      </div>
    </Container>
  );
};

// ============================================
// TELA: TERMO DE PARCERIA (Estabelecimento)
// ============================================

const PartnershipTermsScreen = ({ onBack }) => {
  const Section = ({ title, children }) => (
    <div style={{ marginBottom: spacing.xl }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', color: colors.black, marginBottom: spacing.sm, borderLeft: `4px solid ${colors.gold}`, paddingLeft: spacing.sm }}>
        {title}
      </h3>
      <div style={{ fontSize: '14px', color: colors.grayDark, lineHeight: '1.8' }}>
        {children}
      </div>
    </div>
  );

  return (
    <Container>
      <Header title="Termo de Parceria" onBack={onBack} />
      <div style={{ padding: spacing.lg, paddingBottom: spacing.xxl }}>
        <div style={{
          padding: spacing.lg,
          background: `linear-gradient(135deg, ${colors.gold}20, ${colors.goldLight}20)`,
          border: `2px solid ${colors.gold}`,
          borderRadius: '12px',
          marginBottom: spacing.xl,
          textAlign: 'center'
        }}>
          <span style={{ fontSize: '32px' }}>🤝</span>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors.black, margin: `${spacing.sm} 0` }}>
            Termo de Parceria EAT
          </h2>
          <p style={{ fontSize: '13px', color: colors.grayDark }}>
            Entre EAT Tecnologia Ltda. e o Estabelecimento Parceiro
          </p>
        </div>

        <Section title="1. Objeto da parceria">
          <p>O presente termo regula a relação entre o EAT e o estabelecimento parceiro para fins de disponibilização de QR Code nas dependências do estabelecimento, permitindo que clientes realizem check-in no aplicativo EAT.</p>
        </Section>

        <Section title="2. Obrigações do estabelecimento">
          <ul style={{ paddingLeft: spacing.lg }}>
            <li>Disponibilizar o QR Code em local visível do estabelecimento</li>
            <li>Não modificar ou reproduzir o QR Code fora do contexto autorizado</li>
            <li>Informar ao EAT qualquer uso indevido identificado</li>
            <li>Manter o estabelecimento em conformidade com as leis aplicáveis</li>
          </ul>
        </Section>

        <Section title="3. Isenção de responsabilidade do estabelecimento">
          <div style={{ padding: spacing.md, backgroundColor: colors.gray, borderRadius: '8px' }}>
            <p>O estabelecimento parceiro:</p>
            <ul style={{ paddingLeft: spacing.lg, marginTop: spacing.sm }}>
              <li><strong>Não é parte</strong> das interações entre usuários do aplicativo</li>
              <li><strong>Não tem acesso</strong> a dados pessoais dos usuários</li>
              <li><strong>Não é responsável</strong> pelo conteúdo trocado entre usuários</li>
              <li><strong>Não é responsável</strong> por interações decorrentes do uso do app em suas dependências</li>
              <li>Apenas disponibiliza o QR Code como ponto de acesso físico</li>
            </ul>
          </div>
          <p style={{ marginTop: spacing.sm }}>O EAT é o único responsável pelo funcionamento da plataforma e por eventuais problemas com usuários.</p>
        </Section>

        <Section title="4. Obrigações do EAT">
          <ul style={{ paddingLeft: spacing.lg }}>
            <li>Fornecer suporte técnico para o QR Code</li>
            <li>Moderar ativamente o conteúdo da plataforma</li>
            <li>Responder a denúncias e manter sistema de segurança</li>
            <li>Não expor dados do estabelecimento ou de seus clientes a terceiros</li>
            <li>Arcar com quaisquer responsabilidades decorrentes do uso do aplicativo</li>
          </ul>
        </Section>

        <Section title="5. Propriedade do QR Code">
          <p>O QR Code gerado para o estabelecimento é de propriedade do EAT, vinculado ao CNPJ/CPF do parceiro cadastrado. É vedada a transferência, venda ou uso do QR Code por terceiros.</p>
        </Section>

        <Section title="6. Rescisão">
          <p>Qualquer das partes pode encerrar a parceria com aviso prévio de 15 dias. Em caso de uso indevido pelo estabelecimento, o EAT pode encerrar imediatamente.</p>
        </Section>

        <Section title="7. Confidencialidade">
          <p>As partes se comprometem a manter sigilo sobre informações confidenciais trocadas durante a parceria, pelo prazo de 2 anos após o encerramento.</p>
        </Section>

        <div style={{
          padding: spacing.lg,
          border: `1px solid ${colors.gray}`,
          borderRadius: '12px',
          marginTop: spacing.xl
        }}>
          <p style={{ fontSize: '13px', color: colors.grayDark, marginBottom: spacing.md }}>
            Ao utilizar o QR Code do EAT em seu estabelecimento, o parceiro declara ter lido e concordado com todos os termos acima.
          </p>
          <p style={{ fontSize: '12px', color: colors.grayDark }}>
            Dúvidas: <strong style={{ color: colors.blue }}>parceiros@eat.app</strong>
          </p>
        </div>
      </div>
    </Container>
  );
};

// ============================================
// TELA: DENÚNCIA (Report em 1 clique)
// ============================================

const ReportScreen = ({ person, onSubmit, onBack }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [alsoBlock, setAlsoBlock] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const reasons = [
    { id: 'harassment', icon: '🚫', label: 'Assédio ou comportamento inapropriado' },
    { id: 'fake', icon: '🎭', label: 'Perfil falso ou fraude' },
    { id: 'spam', icon: '📨', label: 'Spam ou conteúdo indesejado' },
    { id: 'threat', icon: '⚠️', label: 'Ameaça ou intimidação' },
    { id: 'underage', icon: '🔞', label: 'Suspeita de menor de idade' },
    { id: 'other', icon: '📝', label: 'Outro motivo' }
  ];

  const handleSubmit = () => {
    // Log da denúncia - em produção enviaria ao backend
    const report = {
      reportedUserId: person.id,
      reason: selectedReason,
      alsoBlock,
      timestamp: new Date().toISOString(),
      reporterDeviceId: 'device_xxx' // viria do sistema
    };
    console.log('Denúncia registrada:', report);
    setSubmitted(true);
    setTimeout(() => onSubmit && onSubmit(), 2500);
  };

  if (submitted) {
    return (
      <Container>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: spacing.xl,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '64px', marginBottom: spacing.lg }}>✅</div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: colors.black, marginBottom: spacing.sm }}>
            Denúncia registrada
          </h2>
          <p style={{ fontSize: '15px', color: colors.grayDark, lineHeight: '1.6' }}>
            Nossa equipe irá analisar em até 24h. {alsoBlock ? `${person.name.split(' ')[0]} também foi bloqueado(a).` : ''}
          </p>
          <div style={{
            marginTop: spacing.lg,
            padding: spacing.md,
            backgroundColor: colors.gray,
            borderRadius: '12px',
            fontSize: '13px',
            color: colors.grayDark
          }}>
            <Shield size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
            Sua denúncia é confidencial. O usuário não será notificado.
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header title="Reportar usuário" onBack={onBack} />
      <div style={{ padding: spacing.lg }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.md,
          padding: spacing.md,
          backgroundColor: colors.gray,
          borderRadius: '12px',
          marginBottom: spacing.xl
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: colors.blue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.white,
            fontWeight: '700',
            fontSize: '18px'
          }}>
            {(person.name || '?').charAt(0)}
          </div>
          <div>
            <p style={{ fontSize: '16px', fontWeight: '600', color: colors.black, margin: 0 }}>{person.name}</p>
            <p style={{ fontSize: '13px', color: colors.grayDark, margin: 0 }}>ID: {person.id}</p>
          </div>
        </div>

        <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.black, marginBottom: spacing.md }}>
          Motivo da denúncia:
        </h3>

        {reasons.map((reason) => (
          <div
            key={reason.id}
            onClick={() => setSelectedReason(reason.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.md,
              padding: spacing.md,
              backgroundColor: selectedReason === reason.id ? colors.red + '10' : colors.gray,
              border: `2px solid ${selectedReason === reason.id ? colors.red : 'transparent'}`,
              borderRadius: '12px',
              marginBottom: spacing.sm,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <span style={{ fontSize: '20px' }}>{reason.icon}</span>
            <span style={{ fontSize: '15px', color: colors.black }}>{reason.label}</span>
            {selectedReason === reason.id && (
              <span style={{ marginLeft: 'auto', color: colors.red, fontWeight: '700' }}>✓</span>
            )}
          </div>
        ))}

        <div
          onClick={() => setAlsoBlock(!alsoBlock)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            padding: spacing.md,
            backgroundColor: alsoBlock ? colors.red + '10' : colors.gray,
            border: `2px solid ${alsoBlock ? colors.red : 'transparent'}`,
            borderRadius: '12px',
            marginTop: spacing.md,
            marginBottom: spacing.xl,
            cursor: 'pointer'
          }}
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            border: `2px solid ${alsoBlock ? colors.red : colors.grayDark}`,
            backgroundColor: alsoBlock ? colors.red : colors.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {alsoBlock && <span style={{ color: colors.white, fontSize: '14px', fontWeight: '700' }}>✓</span>}
          </div>
          <div>
            <p style={{ fontSize: '15px', fontWeight: '600', color: colors.black, margin: 0 }}>Bloquear também</p>
            <p style={{ fontSize: '13px', color: colors.grayDark, margin: 0 }}>{person.name.split(' ')[0]} não poderá mais te ver ou contatar</p>
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          fullWidth
          disabled={!selectedReason}
          style={{ backgroundColor: colors.red, marginBottom: spacing.sm }}
        >
          Enviar denúncia
        </Button>

        <p style={{ fontSize: '12px', color: colors.grayDark, textAlign: 'center', lineHeight: '1.5' }}>
          <Shield size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          Sua identidade é protegida. O usuário não saberá que você denunciou.
        </p>
      </div>
    </Container>
  );
};

// ============================================
// TELA: EXCLUIR CONTA (LGPD)
// ============================================

const DeleteAccountScreen = ({ onBack, onConfirmDelete }) => {
  const [step, setStep] = useState(1);
  const [understood, setUnderstood] = useState(false);

  if (step === 2) {
    return (
      <Container>
        <Header title="Confirmar exclusão" onBack={() => setStep(1)} />
        <div style={{ padding: spacing.xl, textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: spacing.lg }}>⚠️</div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: colors.red, marginBottom: spacing.sm }}>
            Tem certeza absoluta?
          </h2>
          <p style={{ fontSize: '15px', color: colors.grayDark, lineHeight: '1.6', marginBottom: spacing.xl }}>
            Esta ação é <strong>irreversível</strong>. Todos os seus dados serão eliminados permanentemente em até 30 dias, conforme a LGPD.
          </p>
          <Button
            onClick={onConfirmDelete}
            fullWidth
            style={{ backgroundColor: colors.red, marginBottom: spacing.md }}
          >
            Sim, excluir minha conta permanentemente
          </Button>
          <Button variant="secondary" onClick={onBack} fullWidth>
            Cancelar — manter minha conta
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header title="Excluir conta" onBack={onBack} />
      <div style={{ padding: spacing.lg }}>
        <div style={{
          padding: spacing.lg,
          backgroundColor: colors.red + '10',
          border: `2px solid ${colors.red}`,
          borderRadius: '12px',
          marginBottom: spacing.xl
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
            <AlertCircle size={20} color={colors.red} />
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: colors.red, margin: 0 }}>
              Ação irreversível
            </h3>
          </div>
          <p style={{ fontSize: '14px', color: colors.black, lineHeight: '1.6', margin: 0 }}>
            Ao excluir sua conta, todos os seus dados serão permanentemente removidos.
          </p>
        </div>

        <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.black, marginBottom: spacing.md }}>
          O que será excluído:
        </h3>

        {[
          { icon: '👤', text: 'Perfil e dados pessoais', detail: 'Excluídos em até 30 dias (LGPD)' },
          { icon: '💬', text: 'Histórico de conversas', detail: 'Excluído imediatamente' },
          { icon: '📍', text: 'Histórico de check-ins', detail: 'Excluído em até 30 dias' },
          { icon: '⭐', text: 'Avaliações recebidas', detail: 'Excluídas permanentemente' },
          { icon: '🎁', text: 'Bônus de indicação e Premium', detail: 'Não reembolsável' },
        ].map((item) => (
          <div key={item.icon} style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            padding: spacing.md,
            borderBottom: `1px solid ${colors.gray}`
          }}>
            <span style={{ fontSize: '24px' }}>{item.icon}</span>
            <div>
              <p style={{ fontSize: '15px', fontWeight: '600', color: colors.black, margin: 0 }}>{item.text}</p>
              <p style={{ fontSize: '13px', color: colors.grayDark, margin: 0 }}>{item.detail}</p>
            </div>
          </div>
        ))}

        <div style={{ marginTop: spacing.lg, marginBottom: spacing.xl, fontSize: '13px', color: colors.grayDark, lineHeight: '1.6' }}>
          <strong style={{ color: colors.black }}>Exceção (obrigação legal):</strong> Logs de denúncias e interações flagradas são mantidos por 5 anos conforme exigência legal, mesmo após exclusão da conta.
        </div>

        <div
          onClick={() => setUnderstood(!understood)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: spacing.md,
            padding: spacing.md,
            backgroundColor: understood ? colors.red + '10' : colors.gray,
            border: `2px solid ${understood ? colors.red : 'transparent'}`,
            borderRadius: '12px',
            marginBottom: spacing.lg,
            cursor: 'pointer'
          }}
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            border: `2px solid ${understood ? colors.red : colors.grayDark}`,
            backgroundColor: understood ? colors.red : colors.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: '2px'
          }}>
            {understood && <span style={{ color: colors.white, fontSize: '14px', fontWeight: '700' }}>✓</span>}
          </div>
          <span style={{ fontSize: '14px', color: colors.black, lineHeight: '1.5' }}>
            Entendo que esta ação é permanente e não pode ser desfeita.
          </span>
        </div>

        <Button
          onClick={() => setStep(2)}
          fullWidth
          disabled={!understood}
          style={{ backgroundColor: colors.red }}
        >
          Prosseguir com exclusão
        </Button>
      </div>
    </Container>
  );
};

// ============================================
// TELA: PREMIUM (assinatura)
// ============================================

const PremiumScreen = ({ onSubscribe, onBack, loading, error, currentUser }) => {
  const [paymentProvider, setPaymentProvider] = useState('stripe');
  const [mockPaying, setMockPaying] = useState(false);

  const handleMockPay = async () => {
    setMockPaying(true);
    // Simulação de pagamento (bypass para testes)
    await new Promise(r => setTimeout(r, 1500));
    onSubscribe({ paymentProvider, paymentId: 'mock_pi_' + Date.now() });
  };

  const perks = [
    { icon: '💬', text: 'Mensagens ilimitadas em todas as conversas' },
    { icon: '⚡', text: 'Acesso prioritário a novas funcionalidades' },
    { icon: '🌟', text: 'Badge Premium visível no seu perfil' },
    { icon: '🔒', text: 'Sem anúncios — experiência limpa' },
  ];

  return (
    <Container>
      <Header title="Premium" onBack={onBack} />
      <div style={{ padding: spacing.lg }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', padding: `${spacing.xl} ${spacing.md}`, background: `linear-gradient(135deg, ${colors.gold}22, ${colors.blue}11)`, borderRadius: '20px', marginBottom: spacing.xl }}>
          <div style={{ fontSize: '48px', marginBottom: spacing.md }}>👑</div>
          <h2 style={{ fontSize: '26px', fontWeight: '800', color: colors.black, margin: 0, marginBottom: spacing.sm }}>EAT Premium</h2>
          <div style={{ fontSize: '36px', fontWeight: '700', color: colors.gold, marginBottom: spacing.xs }}>R$ 9,90</div>
          <div style={{ fontSize: '14px', color: colors.grayDark }}>por mês · cancele quando quiser</div>
        </div>

        {/* Benefícios */}
        <div style={{ marginBottom: spacing.xl }}>
          {perks.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: spacing.md, padding: `${spacing.md} 0`, borderBottom: i < perks.length - 1 ? `1px solid ${colors.gray}` : 'none' }}>
              <span style={{ fontSize: '22px' }}>{p.icon}</span>
              <span style={{ fontSize: '15px', color: colors.black }}>{p.text}</span>
            </div>
          ))}
        </div>

        {/* Usuário premium — status atual */}
        {currentUser?.premium && (
          <div style={{ padding: spacing.lg, backgroundColor: colors.green + '15', borderRadius: '14px', marginBottom: spacing.lg, textAlign: 'center' }}>
            <span style={{ fontSize: '16px', color: colors.green, fontWeight: '600' }}>✓ Você já é Premium!</span>
          </div>
        )}

        {!currentUser?.premium && (
          <>
            {error && (
              <div style={{ padding: spacing.md, backgroundColor: colors.red + '15', borderRadius: '12px', marginBottom: spacing.md, display: 'flex', gap: spacing.sm }}>
                <AlertCircle size={16} color={colors.red} />
                <span style={{ fontSize: '14px', color: colors.red }}>{error}</span>
              </div>
            )}

            {/* Botão pagamento (modo demo) */}
            <div style={{ padding: spacing.md, backgroundColor: colors.gray, borderRadius: '12px', marginBottom: spacing.md }}>
              <p style={{ fontSize: '12px', color: colors.grayDark, marginBottom: spacing.sm, textAlign: 'center' }}>Modo demonstração — sem cobrança real</p>
              <Button fullWidth onClick={handleMockPay} disabled={mockPaying || loading} style={{ backgroundColor: colors.gold }}>
                {mockPaying || loading ? 'Processando...' : '✨ Assinar agora — R$ 9,90/mês'}
              </Button>
            </div>

            <p style={{ fontSize: '12px', color: colors.grayDark, textAlign: 'center', lineHeight: '1.6' }}>
              Em produção: integração com Stripe ou Pagar.me. Cancele a qualquer momento em Configurações → Assinatura.
            </p>
          </>
        )}
      </div>
    </Container>
  );
};

// ============================================
// TELA: ENTRADA DE TELEFONE (nova)
// ============================================

const PhoneInputScreen = ({ onSend, loading, error }) => {
  const [phone, setPhone] = useState('');

  function formatPhone(raw) {
    const digits = raw.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2)  return digits;
    if (digits.length <= 7)  return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
    if (digits.length <= 11) return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
    return raw;
  }

  function toE164(formatted) {
    const digits = formatted.replace(/\D/g, '');
    return '+55' + digits;
  }

  const isValid = phone.replace(/\D/g, '').length >= 10;

  return (
    <Container>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: spacing.xl, textAlign: 'center' }}>

        <div style={{ width: '80px', height: '80px', borderRadius: '20px', backgroundColor: colors.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: spacing.xl }}>
          <Phone size={40} color={colors.white} />
        </div>

        <h1 style={{ fontSize: '26px', fontWeight: '700', color: colors.black, marginBottom: spacing.sm }}>
          Qual é o seu número?
        </h1>
        <p style={{ fontSize: '15px', color: colors.grayDark, marginBottom: spacing.xl, lineHeight: '1.6' }}>
          Vamos enviar um código por SMS para verificar sua identidade.
        </p>

        <div style={{ width: '100%', marginBottom: spacing.lg }}>
          <div style={{ display: 'flex', alignItems: 'center', border: `2px solid ${error ? colors.red : isValid ? colors.blue : colors.gray}`, borderRadius: '14px', padding: `${spacing.md} ${spacing.lg}`, backgroundColor: colors.gray, gap: spacing.sm, transition: 'all 0.2s' }}>
            <span style={{ fontSize: '22px' }}>🇧🇷</span>
            <span style={{ fontSize: '16px', color: colors.grayDark, fontWeight: '600' }}>+55</span>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="(11) 99999-9999"
              value={phone}
              onChange={e => setPhone(formatPhone(e.target.value))}
              onKeyDown={e => { if (e.key === 'Enter' && isValid && !loading) onSend(toE164(phone)); }}
              style={{ flex: 1, border: 'none', background: 'transparent', fontSize: '18px', fontWeight: '600', color: colors.black, outline: 'none' }}
              autoFocus
            />
          </div>

          {error && (
            <div style={{ marginTop: spacing.sm, padding: spacing.md, backgroundColor: colors.red + '15', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              <AlertCircle size={16} color={colors.red} />
              <span style={{ fontSize: '14px', color: colors.red }}>{error}</span>
            </div>
          )}
        </div>

        <Button fullWidth onClick={() => onSend(toE164(phone))} disabled={!isValid || loading}>
          {loading ? 'Enviando...' : 'Enviar código SMS →'}
        </Button>

        <p style={{ marginTop: spacing.lg, fontSize: '12px', color: colors.grayDark, lineHeight: '1.6' }}>
          Ao continuar, você concorda com o uso do seu número para autenticação. Não compartilhamos com terceiros.
        </p>
      </div>
    </Container>
  );
};

// ============================================
// TELA: VERIFICAÇÃO OTP (nova)
// ============================================

const OTPVerifyScreen = ({ phone, onVerify, onResend, loading, error }) => {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(60);
  const inputRefs = Array.from({ length: 6 }, () => React.createRef());

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  function handleDigit(idx, val) {
    const char = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = char;
    setDigits(next);
    if (char && idx < 5) inputRefs[idx + 1].current?.focus();
    if (next.every(d => d !== '') && next.join('').length === 6) {
      onVerify(next.join(''));
    }
  }

  function handleKeyDown(idx, e) {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs[idx - 1].current?.focus();
    }
  }

  function handlePaste(e) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const next = pasted.split('');
      setDigits(next);
      onVerify(pasted);
    }
  }

  const code = digits.join('');

  const maskedPhone = phone
    ? phone.replace('+55', '').replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    : '';

  return (
    <Container>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: spacing.xl, textAlign: 'center' }}>

        <div style={{ width: '80px', height: '80px', borderRadius: '20px', backgroundColor: colors.green, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: spacing.xl }}>
          <Lock size={40} color={colors.white} />
        </div>

        <h1 style={{ fontSize: '26px', fontWeight: '700', color: colors.black, marginBottom: spacing.sm }}>
          Código SMS
        </h1>
        <p style={{ fontSize: '15px', color: colors.grayDark, marginBottom: spacing.xl, lineHeight: '1.6' }}>
          Enviamos um código de 6 dígitos para<br />
          <strong style={{ color: colors.black }}>{maskedPhone}</strong>
        </p>

        {/* Campos de dígito */}
        <div style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.lg }} onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={inputRefs[i]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={e => handleDigit(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              autoFocus={i === 0}
              style={{
                width: '48px',
                height: '60px',
                borderRadius: '12px',
                border: `2px solid ${error ? colors.red : d ? colors.blue : colors.gray}`,
                backgroundColor: d ? colors.blue + '15' : colors.gray,
                fontSize: '24px',
                fontWeight: '700',
                textAlign: 'center',
                color: colors.black,
                outline: 'none',
                transition: 'all 0.15s'
              }}
            />
          ))}
        </div>

        {error && (
          <div style={{ marginBottom: spacing.lg, padding: spacing.md, backgroundColor: colors.red + '15', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: spacing.sm, width: '100%' }}>
            <AlertCircle size={16} color={colors.red} />
            <span style={{ fontSize: '14px', color: colors.red }}>{error}</span>
          </div>
        )}

        <Button fullWidth onClick={() => onVerify(code)} disabled={code.length < 6 || loading} style={{ marginBottom: spacing.md }}>
          {loading ? 'Verificando...' : 'Verificar →'}
        </Button>

        <button
          onClick={() => { onResend(); setResendTimer(60); setDigits(['','','','','','']); }}
          disabled={resendTimer > 0}
          style={{ background: 'none', border: 'none', fontSize: '14px', color: resendTimer > 0 ? colors.grayDark : colors.blue, cursor: resendTimer > 0 ? 'default' : 'pointer', fontWeight: '600' }}
        >
          {resendTimer > 0 ? `Reenviar em ${resendTimer}s` : 'Reenviar código'}
        </button>
      </div>
    </Container>
  );
};

// ============================================
// APP PRINCIPAL - DEMONSTRAÇÃO
// ============================================

const EATApp = () => {
  const [currentScreen, setCurrentScreen] = useState('consent');
  const [userStatus, setUserStatus] = useState('green');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [affiliateData, setAffiliateData] = useState(null);

  // ── Auth state ──────────────────────────────────────────────────────────
  const [authToken, setAuthToken]       = useState(() => localStorage.getItem('eat_token') || null);
  const [currentUser, setCurrentUser]   = useState(null);
  const [currentVenue, setCurrentVenue] = useState(null);
  const [pendingPhone, setPendingPhone] = useState('');

  // ── Socket.io ─────────────────────────────────────────────────────────────
  const socketRef = useRef(null);

  // ── Chat / conversa ───────────────────────────────────────────────────────
  const [activeConversationId, setActiveConversationId] = useState(null);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError]     = useState(null);

  // ── Persiste token no localStorage ───────────────────────────────────────
  useEffect(() => {
    if (authToken) localStorage.setItem('eat_token', authToken);
    else localStorage.removeItem('eat_token');
  }, [authToken]);

  // ── Conecta Socket.io após login ──────────────────────────────────────────
  useEffect(() => {
    if (!authToken) { if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; } return; }
    // socket.io-client deve estar disponível globalmente via CDN ou import
    if (typeof window.io === 'undefined') {
      console.warn('[Socket] socket.io-client não encontrado. Chat em tempo real desabilitado.');
      return;
    }
    const socket = window.io(API_BASE, {
      auth: { token: authToken },
      transports: ['websocket', 'polling'],
    });
    socket.on('connect',       () => console.log('[Socket] Conectado'));
    socket.on('connect_error', (e) => console.warn('[Socket] Erro:', e.message));
    socketRef.current = socket;
    return () => { socket.disconnect(); socketRef.current = null; };
  }, [authToken]);

  // ── Firebase: renova token automaticamente ────────────────────────────────────
  useEffect(() => {
    const { onIdTokenChanged } = require('firebase/auth');
    const unsub = onIdTokenChanged(firebaseAuth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        setAuthToken(token);
        localStorage.setItem('eat_token', token);
      }
    });
    return () => unsub();
  }, []);

  // ── Carrega perfil ao abrir app com token salvo ───────────────────────────
  useEffect(() => {
    if (!authToken) return;
    api.get('/users/me', authToken)
      .then(user => {
        setCurrentUser(user);
        setCurrentScreen('qr');
      })
      .catch(() => {
        // Token expirado — Firebase renova automaticamente via onIdTokenChanged
        setAuthToken(null);
        localStorage.removeItem('eat_token');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mock fallback (usado em telas que ainda não consomem API real) ────────
  const mockUser = currentUser || {
    id: 'user123',
    name: 'Você',
    premium: false,
    abacaxi: true,
    arcoIris: false,
    rating: 4.7,
    totalRatings: 23,
    referralCount: 2,
    referralBonus: 6,
    referralCode: 'EATXYZ123',
    deviceId: getDeviceId(),
    consentedAt: null,
  };

  const mockPeople = [
    { id: 1, name: 'Ana Silva', status: 'green', abacaxi: true, arcoIris: false, premium: true },
    { id: 2, name: 'Carlos Santos', status: 'yellow', abacaxi: false, arcoIris: true, premium: false },
    { id: 3, name: 'Marina Costa', status: 'green', abacaxi: true, arcoIris: false, premium: false },
    { id: 4, name: 'Pedro Lima', status: 'red', abacaxi: false, arcoIris: false, premium: true },
    { id: 5, name: 'Julia Oliveira', status: 'green', abacaxi: false, arcoIris: true, premium: false }
  ];

  const mockMessages = [
    { id: 1, content: 'Oi! Vi que você também está aqui 😊', isMine: false, time: '19:30' },
    { id: 2, content: 'Oi! Sim, acabei de chegar. Você vem sempre aqui?', isMine: true, time: '19:32' }
  ];

  // ── Handlers de autenticação ─────────────────────────────────────────────

  const handleConsentAccept = useCallback(async () => {
    setApiError(null);
    setApiLoading(true);
    try {
      await api.post('/consents', {
        checkedAge: true,
        checkedPrivacy: true,
        checkedTerms: true,
        checkedLocation: true,
        deviceId: getDeviceId(),
      }, authToken);
    } catch (e) {
      // Falha silenciosa — o consentimento local ainda vale
      console.warn('[Consent API]', e.message);
    } finally {
      setApiLoading(false);
    }
    setCurrentScreen('phone-input');
  }, [authToken]);

  const handleSendOtp = useCallback(async (phone) => {
    setApiError(null);
    setApiLoading(true);
    try {
      // Limpa verifier anterior
      if (window._eatRecaptchaVerifier) {
        try { window._eatRecaptchaVerifier.clear(); } catch (_) {}
        window._eatRecaptchaVerifier = null;
      }

      // Container fora do React (evita conflito com re-renders)
      const oldEl = document.getElementById('recaptcha-portal');
      if (oldEl) document.body.removeChild(oldEl);
      const el = document.createElement('div');
      el.id = 'recaptcha-portal';
      document.body.appendChild(el);

      // SDK modular Firebase (sem CDN, sem bugs de null.style)
      window._eatRecaptchaVerifier = new RecaptchaVerifier(
        firebaseAuth,
        'recaptcha-portal',
        { size: 'invisible' }
      );

      const confirmationResult = await signInWithPhoneNumber(
        firebaseAuth, phone, window._eatRecaptchaVerifier
      );
      window._eatConfirmation = confirmationResult;
      setPendingPhone(phone);
      setCurrentScreen('otp-verify');
    } catch (e) {
      setApiError(e.message || 'Erro ao enviar SMS. Tente novamente.');
    } finally {
      setApiLoading(false);
    }
  }, []);

  const handleVerifyOtp = useCallback(async (code) => {
    setApiError(null);
    setApiLoading(true);
    try {
      if (!window._eatConfirmation) {
        throw new Error('Sessão expirada. Solicite um novo código.');
      }
      // Firebase valida o OTP e retorna o usuário autenticado
      const result  = await window._eatConfirmation.confirm(code);
      const idToken = await result.user.getIdToken();

      // Sincroniza/cria perfil no Firestore via backend
      const data = await api.post('/auth/sync', { deviceId: getDeviceId() }, idToken);

      setAuthToken(idToken);
      localStorage.setItem('eat_token', idToken);
      setCurrentUser(data.user);
      setCurrentScreen(data.isNewUser ? 'affiliate-onboarding' : 'qr');
    } catch (e) {
      setApiError(e.message || 'Código inválido. Tente novamente.');
    } finally {
      setApiLoading(false);
    }
  }, []);

  const handleQRScan = useCallback(async (qrCode) => {
    // qrCode = undefined em modo demo → vai direto para home com mock
    setApiError(null);
    if (!authToken || !qrCode) {
      setCurrentVenue({ id: 'mock', name: 'Bar do João', city: 'São Paulo' });
      setCurrentScreen('home');
      return;
    }
    setApiLoading(true);
    try {
      const venue = await api.get(`/venues/qr/${qrCode}`, authToken);
      setCurrentVenue(venue);
      await api.post('/checkins', { venueId: venue.id }, authToken);
      setCurrentScreen('home');
    } catch (e) {
      setApiError(e.message || 'QR Code inválido ou estabelecimento não encontrado.');
    } finally {
      setApiLoading(false);
    }
  }, [authToken]);

  const handleSendMessage = useCallback(async (conversationId, content) => {
    if (!authToken || !conversationId) {
      console.log('[Chat mock] Enviar:', content);
      return;
    }
    try {
      await api.post(`/conversations/${conversationId}/messages`, { content }, authToken);
    } catch (e) {
      if (e.statusCode === 402) {
        setCurrentScreen('premium');
      } else {
        console.error('[Chat]', e.message);
      }
    }
  }, [authToken]);

  const handleStartChat = useCallback(async (person) => {
    setSelectedPerson(person);
    if (authToken && person?.id) {
      try {
        const data = await api.post('/conversations', { recipientId: person.id }, authToken);
        setActiveConversationId(data.id);
      } catch (e) {
        console.warn('[Conversation]', e.message);
        setActiveConversationId(null);
      }
    } else {
      setActiveConversationId(null);
    }
    setCurrentScreen('chat');
  }, [authToken]);

  const handleSubscribe = useCallback(async ({ paymentProvider, paymentId }) => {
    setApiError(null);
    setApiLoading(true);
    try {
      if (authToken) {
        await api.post('/payments/subscribe', { paymentProvider, paymentId }, authToken);
        const updatedUser = await api.get('/users/me', authToken);
        setCurrentUser(updatedUser);
      } else {
        // Modo demo sem auth
        setCurrentUser(u => u ? { ...u, premium: true } : u);
      }
      setCurrentScreen('chat');
    } catch (e) {
      setApiError(e.message || 'Erro ao processar pagamento.');
    } finally {
      setApiLoading(false);
    }
  }, [authToken]);

  const handleStatusChange = useCallback(async (status) => {
    setUserStatus(status);
    if (authToken) {
      try {
        await api.put('/users/me/status', { status }, authToken);
      } catch (e) {
        console.warn('[Status API]', e.message);
      }
    }
  }, [authToken]);

  const handleToggleSymbol = useCallback(async (symbol) => {
    if (!authToken || !currentUser) return;
    const newValue = !currentUser[symbol];
    // Atualiza localmente primeiro (UI responsiva)
    setCurrentUser(prev => ({ ...prev, [symbol]: newValue }));
    try {
      await api.put('/users/me', { [symbol]: newValue }, authToken);
    } catch (e) {
      // Reverte se falhar
      setCurrentUser(prev => ({ ...prev, [symbol]: !newValue }));
    }
  }, [authToken, currentUser]);

  const handleLogout = useCallback(async () => {
    if (authToken) {
      try { await api.del('/checkins/me', authToken); } catch {}
    }
    // Faz logout do Firebase Auth
    try { await signOut(firebaseAuth); } catch (_) {}
    setAuthToken(null);
    setCurrentUser(null);
    setCurrentVenue(null);
    localStorage.removeItem('eat_token');
    setCurrentScreen('consent');
  }, [authToken]);

  // ── Render ────────────────────────────────────────────────────────────────

  const renderScreen = () => {
    switch (currentScreen) {
      case 'consent':
        return (
          <ConsentScreen
            onAccept={handleConsentAccept}
            onDecline={() => alert('Você precisa aceitar os termos para usar o EAT.')}
          />
        );

      case 'phone-input':
        return (
          <PhoneInputScreen
            onSend={handleSendOtp}
            loading={apiLoading}
            error={apiError}
          />
        );

      case 'otp-verify':
        return (
          <OTPVerifyScreen
            phone={pendingPhone}
            onVerify={handleVerifyOtp}
            onResend={() => handleSendOtp(pendingPhone)}
            loading={apiLoading}
            error={apiError}
          />
        );

      case 'privacy':
        return <PrivacyPolicyScreen onBack={() => setCurrentScreen('settings')} />;

      case 'terms':
        return <TermsOfUseScreen onBack={() => setCurrentScreen('settings')} />;

      case 'partnership':
        return <PartnershipTermsScreen onBack={() => setCurrentScreen('settings')} />;

      case 'report':
        return (
          <ReportScreen
            person={selectedPerson || mockPeople[0]}
            onSubmit={() => setCurrentScreen('home')}
            onBack={() => setCurrentScreen('profile')}
          />
        );

      case 'delete-account':
        return (
          <DeleteAccountScreen
            onBack={() => setCurrentScreen('settings')}
            onConfirmDelete={async () => {
              if (authToken) {
                try { await api.del('/users/me', authToken); } catch (e) { console.warn(e); }
              }
              setAuthToken(null);
              setCurrentUser(null);
              setCurrentVenue(null);
              alert('Conta marcada para exclusão. Seus dados serão removidos em até 30 dias conforme LGPD Art. 18.');
              setCurrentScreen('consent');
            }}
          />
        );

      case 'affiliate-onboarding':
        return (
          <AffiliateOnboardingScreen
            onContinue={(data) => {
              setAffiliateData({ ...data, status: 'pending', affiliateCode: 'EAT-' + data.instagramHandle.replace('@','').toUpperCase().slice(0,5) + '-' + Math.floor(Math.random()*900+100), monthlyEarnings: 0, usersReferred: 0, history: [0,0,0,0,0,0], pendingPayout: 0, nextPayoutDate: '05/03/2026' });
              setCurrentScreen('qr');
            }}
            onSkip={() => setCurrentScreen('qr')}
          />
        );

      case 'affiliate-panel':
        return (
          <AffiliatePanelScreen
            affiliate={affiliateData}
            onBack={() => setCurrentScreen('settings')}
          />
        );

      case 'qr':
        return (
          <QRScanScreen
            onScan={() => handleQRScan(undefined)}
          />
        );

      case 'home':
        return (
          <HomeScreen
            location={currentVenue?.name || 'Bar do João'}
            userStatus={userStatus}
            user={mockUser}
            onStatusChange={handleStatusChange}
            people={mockPeople}
            onPersonClick={(person) => { setSelectedPerson(person); setCurrentScreen('profile'); }}

          />
        );
      
      case 'profile':
        return (
          <ProfileScreen
            person={selectedPerson || mockPeople[0]}
            onStartChat={() => handleStartChat(selectedPerson || mockPeople[0])}
            onReport={() => setCurrentScreen('report')}
            onBack={() => setCurrentScreen('home')}
          />
        );
      
      case 'chat':
        return (
          <ChatScreen
            person={selectedPerson || mockPeople[0]}
            conversationId={activeConversationId}
            socket={socketRef.current}
            currentUserId={mockUser.id}
            onSendMessage={(msg) => handleSendMessage(activeConversationId, msg)}
            onBack={() => setCurrentScreen('rating')}
            isPremium={mockUser.premium}
            onUpgrade={() => setCurrentScreen('premium')}
          />
        );

      case 'premium':
        return (
          <PremiumScreen
            onBack={() => setCurrentScreen(activeConversationId ? 'chat' : 'settings')}
            onSubscribe={handleSubscribe}
            loading={apiLoading}
            error={apiError}
            currentUser={mockUser}
          />
        );
      
      case 'rating':
        return (
          <RatingScreen
            person={selectedPerson || mockPeople[0]}
            onRate={(rating) => {
              console.log('Avaliação:', rating);
              setCurrentScreen('home');
            }}
            onSkip={() => setCurrentScreen('home')}
          />
        );
      
      case 'referral':
        return (
          <ReferralScreen
            user={mockUser}
            onBack={() => setCurrentScreen('settings')}
            onShare={() => alert('Compartilhar link de indicação')}
          />
        );
      
      case 'my-profile':
        return (
          <MyProfileScreen
            user={currentUser || mockUser}
            onBack={() => setCurrentScreen('settings')}
            onSave={async (updates) => {
              const data = await api.put('/users/me', updates, authToken);
              setCurrentUser(prev => ({ ...prev, ...updates }));
              return data;
            }}
          />
        );

      case 'settings':
        return (
          <SettingsScreen
            user={currentUser || mockUser}
            onBack={() => setCurrentScreen('home')}
            onLogout={handleLogout}
            onToggleSymbol={handleToggleSymbol}
            onOpenMyProfile={() => setCurrentScreen('my-profile')}
            onOpenReferral={() => setCurrentScreen('referral')}
            onOpenPrivacy={() => setCurrentScreen('privacy')}
            onOpenTerms={() => setCurrentScreen('terms')}
            onOpenPartnership={() => setCurrentScreen('partnership')}
            onDeleteAccount={() => setCurrentScreen('delete-account')}
            onOpenAffiliatePanel={() => setCurrentScreen('affiliate-panel')}
          />
        );
      
      default:
        return <QRScanScreen onScan={() => setCurrentScreen('home')} />;
    }
  };

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      minHeight: '100vh',
      backgroundColor: colors.white
    }}>
      {renderScreen()}

      {/* Navigation Demo */}
      <div style={{
        position: 'fixed',
        bottom: spacing.md,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: spacing.xs,
        padding: spacing.sm,
        backgroundColor: colors.black + 'ee',
        borderRadius: '24px',
        zIndex: 1000,
        fontSize: '11px',
        flexWrap: 'wrap',
        maxWidth: '460px',
        justifyContent: 'center'
      }}>
        {[
          ['consent', 'LGPD'],
          ['phone-input', 'FONE'],
          ['otp-verify', 'OTP'],
          ['affiliate-onboarding', 'AFIL.ON'],
          ['affiliate-panel', 'PAINEL'],
          ['qr', 'QR'],
          ['home', 'HOME'],
          ['profile', 'PERFIL'],
          ['chat', 'CHAT'],
          ['rating', 'AVAL.'],
          ['referral', 'INDICAR'],
          ['settings', 'CONFIG'],
          ['report', 'DENÚNCIA'],
          ['privacy', 'PRIVAC.'],
          ['terms', 'TERMOS'],
          ['partnership', 'PARCERIA'],
          ['premium', 'PREMIUM'],
          ['delete-account', 'EXCLUIR'],
        ].map(([screen, label]) => (
          <button
            key={screen}
            onClick={() => setCurrentScreen(screen)}
            style={{
              padding: `${spacing.xs} ${spacing.sm}`,
              backgroundColor: currentScreen === screen ? colors.blue : 'transparent',
              color: colors.white,
              border: 'none',
              borderRadius: '12px',
              fontSize: '9px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default EATApp;
